import { AnimatePresence } from "framer-motion";
import {
  ArrowDownRight, ArrowLeft, ArrowRight, ArrowUpRight, CheckSquare, ChevronDown, CirclePause, LoaderCircle, CloudUpload, Columns3, Copy, CornerDownRight, Film, GitBranch, GitBranchPlus, Languages, Plus,
  Radio, RotateCcw, Search, Sparkles, Split, Square, Trash2, TriangleAlert, Undo2, Upload, X
} from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "../router";
import type { Creative, CreativeFile, CreativeLocalization, CreativeState, Hypothesis, HypothesisStatus, Moderation, PlacementAsset, PlacementAspect, WorkspaceSettings } from "@lovmia/shared";
import {
  creativeStateLabels, deliveryStatusLabels, hypothesisStatusLabels, localeLabels, localeTag, localizationStateLabels, localizationTag,
  mediaLibraryStatusLabels, moderationStatusLabels, moderationVerdict, placementAspectLabels, placementAspectSlug, placementAspects, producers, supportedLocales
} from "@lovmia/shared";
import { ApiError, formatDate, formatMoney, formatPercent, relativeTime, uploadHypothesisSource, uploadLocalizationVideo, uploadPlacementAsset } from "../api";
import { Button, ConfirmDialog, CreativeVisual, DeliveryChip, Modal, ModerationChip, PageHeader, Select, StatusPill, Tag, VerdictPill } from "../components/Ui";
import { CastingStrip } from "../components/Casting";
import { HypothesisControls } from "../components/HypothesisControls";
import { CreativeControls, DeleteCreativesDialog, MoveCreativesDialog, creativeFootprint } from "../components/CreativeControls";
import { isSlimBootstrap, useAppState, useCreativeDetail, useCreativeUpload, useHypothesisDetail } from "../state";
import { currentWeekLabel } from "../week";

type Column = { id: string; title: string; states: CreativeState[]; hint: string };
const columns: Column[] = [
  { id: "backlog", title: "Бэклог", states: [], hint: "ещё без пары" },
  { id: "production", title: "Продакшн", states: ["production"], hint: "делаем файл" },
  { id: "ready", title: "Готово", states: ["ready_for_upload"], hint: "ждёт UA" },
  { id: "live", title: "В открутке (тест 1)", states: ["live", "paused_manual"], hint: "набирает основной гейт" },
  { id: "verdict", title: "Вердикт (тест 1)", states: ["verdict_pending", "version_spawned", "archived", "fatigued"], hint: "нужно решение" },
  // Тест 2 — скейл-прогон руками UA: боевая кампания на бюджет теста 2 по всем
  // кабинетам, ≥3 запусков, 8–12 крео в адсете. Доска регламент не проверяет —
  // только копит дельту спенда и по бюджету переносит крео в вердикт фазы.
  { id: "live2", title: "В открутке (тест 2)", states: ["live_test2"], hint: "скейл-прогон в боевой" },
  { id: "verdict2", title: "Вердикт (тест 2)", states: ["verdict_pending_test2"], hint: "нужно решение" },
  // Виннеры — только ручное решение «Отправить в виннеры» (из вердикта любой
  // фазы): колонка отвечает на единственный вопрос UA — куда спендить бюджет.
  { id: "winners", title: "Виннеры", states: ["scaled"], hint: "сюда льём бюджет" },
  // Лузеры — зеркало виннеров: тест провален, бюджет не льём. Только ручное
  // решение — «В лузеры» из вердикта или вся гипотеза из её меню.
  { id: "losers", title: "Лузеры", states: ["lost"], hint: "тест провален" }
];

/** Колонки вида «Гипотезы»: движение статуса всего концепта, а не исполнений. */
const hypothesisColumns: Array<{ id: string; title: string; statuses: HypothesisStatus[]; hint: string }> = [
  { id: "backlog", title: "Бэклог", statuses: ["backlog", "rework_safe"], hint: "ждут волну" },
  { id: "live", title: "В работе", statuses: ["live"], hint: "пара крутится" },
  { id: "split", title: "Раскол", statuses: ["split"], hint: "исполнение решает" },
  { id: "proven", title: "Proven", statuses: ["proven"], hint: "есть победитель" },
  { id: "lost", title: "Лузеры", statuses: ["lost"], hint: "тест провален" },
  { id: "cold", title: "Холодильник", statuses: ["frozen", "killed"], hint: "можно вернуться" }
];

/**
 * Колонка локали. До кабинета локаль движется своим состоянием — «нет файла»
 * это продакшн, файл есть — «Готово»: иначе «сделай ES-MX» не видно из
 * колонки, где его делают. Уехав в кабинет, локаль живёт судьбой родителя:
 * своего вердикта у неё нет (гейт и пороги посчитаны по EN-медианам), и стоять
 * ей в «Вердикте» отдельной строкой не с чем.
 */
function localizationColumn(item: CreativeLocalization, parent: Creative): string {
  if (item.state === "production") return "production";
  if (item.state === "ready_for_upload") return "ready";
  return columns.find((column) => column.states.includes(parent.state))?.id ?? "live";
}

/**
 * Ветка колонки — крео и его локали. `present` говорит, стоит ли в этой колонке
 * само крео: локаль движется своим состоянием и обгоняет родителя (ES-MX ещё
 * без файла, когда EN уже крутится). Тогда вместо карточки рисуется корешок —
 * строка «чьё это», чтобы семья читалась и там, где родителя нет.
 */
type Branch = { creative: Creative; present: boolean; localizations: CreativeLocalization[] };

/**
 * Крео — единица движения: каждое исполнение стоит в своей колонке, даже если
 * братья по гипотезе ушли вперёд. Гипотеза без крео живёт только в бэклоге.
 * Локали — такая же единица (`flat`): своя карточка со сдвигом под своим крео,
 * а не флажок внутри чужой.
 */
function columnEntries(column: Column, families: Hypothesis[], flat = false) {
  // Гипотеза в бэклоге стоит в первой колонке целиком — и та, что ещё не
  // распакована, и та, которую вернули со всего захода (`return_to_backlog`):
  // её крео к этому моменту в архиве, и раскидывать их по колонкам решений
  // значило бы показывать закрытый заход как живой.
  const backlog = (family: Hypothesis) => !family.creatives.length || family.status === "backlog";
  if (column.id === "backlog") {
    return families.filter(backlog).map((family) => ({
      family, branches: [] as Branch[], creatives: [] as Creative[], localizations: [] as CreativeLocalization[]
    }));
  }
  return families
    .filter((family) => !backlog(family))
    .map((family) => {
      const branches: Branch[] = family.creatives
        .map((creative) => ({
          creative,
          present: column.states.includes(creative.state),
          localizations: flat ? creative.localizations.filter((item) => localizationColumn(item, creative) === column.id) : []
        }))
        .filter((branch) => branch.present || branch.localizations.length > 0);
      return {
        family,
        branches,
        creatives: branches.filter((branch) => branch.present).map((branch) => branch.creative),
        localizations: branches.flatMap((branch) => branch.localizations)
      };
    })
    .filter((entry) => entry.branches.length > 0);
}

/** Человекочитаемый заголовок вариации; для старых записей — ID. */
function creativeTitle(creative: Creative) {
  return creative.variationTitle ? `${creative.id} · ${creative.variationTitle}` : creative.id;
}

function QaChip({ creative }: { creative: Creative }) {
  if (creative.qaVerdict == null) return null;
  const tone = creative.qaVerdict === "PASS" ? "qa-chip--pass" : creative.qaVerdict === "FAIL" ? "qa-chip--fail" : "qa-chip--fix";
  return <span className={`qa-chip ${tone}`}>QA {creative.qaVerdict}{creative.qaScore != null ? ` · ${creative.qaScore}` : ""}</span>;
}

/** Суммарный спенд гипотезы: пара проверяет одно утверждение, бюджет считается на неё целиком. */
function familySpend(family: Hypothesis) {
  return family.creatives.reduce((sum, creative) => sum + creative.metrics.spend, 0);
}

/**
 * Спенд локалей семьи. Деньги настоящие, но в гейт гипотезы не входят: гейт
 * проверяет утверждение на EN-медианах, а у мексиканского гео свои. Поэтому
 * сумма показана отдельной строкой, а не прибавлена к прогрессу.
 */
function familyLocaleSpend(family: Hypothesis) {
  return family.creatives.reduce(
    (sum, creative) => sum + creative.localizations.reduce((inner, item) => inner + item.metrics.spend, 0), 0);
}

/** CPS гипотезы: суммарный спенд на суммарные реальные подписки — рейты складывать нельзя. */
function familyCps(family: Hypothesis) {
  const subscriptions = family.creatives.reduce((sum, creative) => sum + creative.realSubscriptions, 0);
  return subscriptions > 0 ? familySpend(family) / subscriptions : null;
}

/**
 * Модерация семьи: реджекты и запуски всех её объявлений по всем кабинетам,
 * локали наравне с крео. Сумма счётчиков, а не доля «отклонённых крео»: одно
 * отклонённое объявление из десяти не делает крео отклонённым (GEN-2250).
 * null — фид ещё ни о ком из семьи не присылал модерацию.
 */
function familyModeration(family: Hypothesis) {
  const counters = family.creatives
    .flatMap((creative) => [creative.moderation, ...creative.localizations.map((item) => item.moderation)])
    .filter((item): item is Moderation => item != null);
  if (!counters.length) return null;
  return counters.reduce((sum, item) => ({ launched: sum.launched + item.launched, rejected: sum.rejected + item.rejected }), { launched: 0, rejected: 0 });
}

/** Модерация «плохая» — сработал статус: красная рамка у локали и флажка. */
function moderationBad(moderation: Moderation | null, thresholds: WorkspaceSettings["moderation"]) {
  return moderationVerdict(moderation, thresholds).status != null;
}

/**
 * Шапка гипотезы на доске (по макету GEN-1578): прогресс спенда до гейта и ряд
 * spend / cps / reject — решение по семье видно без открытия карточек.
 */
function FamilyStats({ family }: { family: Hypothesis }) {
  const { data } = useAppState();
  const spend = familySpend(family);
  const gate = data.settings.gateSpend;
  const done = spend >= gate;
  const cps = familyCps(family);
  const reject = familyModeration(family);
  const locales = data.settings.flatLocalizations ? familyLocaleSpend(family) : 0;
  return <div className="family-stats" title={`Основной гейт гипотезы: $${gate} на все исполнения вместе`}>
    <div className={`spend-mini ${done ? "spend-mini--done" : ""}`}>
      <div className="spend-mini__bar"><i style={{ width: `${Math.min(100, (spend / gate) * 100)}%` }} /></div>
    </div>
    <div className="stat-row">
      <span><small>spend</small><b>{formatMoney(spend)} / {formatMoney(gate)}</b></span>
      <span><small>cps</small><b className={cps == null ? "stat-zero" : ""}>{formatMoney(cps, 2)}</b></span>
      <span title="Реджекты / запуски объявлений семьи по всем кабинетам"><small>reject</small><b className={reject == null || reject.launched === 0 ? "stat-zero" : reject.rejected > 0 ? "stat-warn" : ""}>{reject ? `${reject.rejected}/${reject.launched}` : "—"}</b></span>
    </div>
    {done && <small className="family-stats__gate">гейт пройден</small>}
    {locales > 0 && <small className="family-stats__locales" title="Локали — отдельные объявления в кабинете: деньги реальные, но в гейт гипотезы не входят, у другого гео другие медианы">
      локали +{formatMoney(locales)}
    </small>}
  </div>;
}

/**
 * Ряд маркетинговых показателей на карточке крео — вместо оценочных тегов.
 * Hook и hold красятся абсолютными порогами из настроек: не ниже порога —
 * зелёный, ниже — красный; нулевые значения нейтральны, это «нет данных»,
 * а не провал.
 */
function CreativeStats({ creative }: { creative: Creative }) {
  const { data } = useAppState();
  const { hookRate, holdRate, spend, cpa } = creative.metrics;
  const tone = (value: number, min: number) => value <= 0 ? "stat-zero" : value >= min ? "stat-good" : "stat-bad";
  const split = spendSplit(creative);
  // Нет данных — «—», а не $0: ряд рендерится на каждой карточке (одинаковая
  // высота в колонке), и нули не должны читаться как измеренный провал.
  return <div className="stat-row stat-row--creative" title={split ? `тест 1 ${formatMoney(split.test1)} · тест 2 ${formatMoney(split.test2)} · всего ${formatMoney(spend)}` : undefined}>
    <span><small>hook</small><b className={tone(hookRate, data.settings.hookRateMin)}>{hookRate > 0 ? formatPercent(hookRate) : "—"}</b></span>
    <span><small>hold</small><b className={tone(holdRate, data.settings.holdRateMin)}>{holdRate > 0 ? formatPercent(holdRate) : "—"}</b></span>
    {split
      ? <span><small>т1 · т2</small><b>{formatMoney(split.test1)} · {formatMoney(split.test2)}</b></span>
      : <span><small>spend</small><b className={spend > 0 ? "" : "stat-zero"}>{spend > 0 ? formatMoney(spend) : "—"}</b></span>}
    <span><small>cps</small><b className={cpa == null ? "stat-zero" : ""}>{formatMoney(cpa, 2)}</b></span>
  </div>;
}

/** Раздельный спенд фаз: т1 — снимок на момент перевода в тест 2, т2 — дельта после него. */
function spendSplit(creative: Creative) {
  if (creative.test2StartSpend == null) return null;
  return { test1: creative.test2StartSpend, test2: Math.max(0, creative.metrics.spend - creative.test2StartSpend) };
}

/**
 * Компактная строка крео (макет GEN-1578). Контекст семьи (название, statement)
 * живёт в шапке группы; вместо оценочных тегов карточка несёт автора с
 * продюсером и ряд hook / hold / spend / cps, окрашенный порогами из настроек.
 * Видео открывается в карточке крео.
 */
function CreativeCard({ creative, family, sibling, onOpen, upload }: { creative: Creative; family: Hypothesis; sibling: boolean; onOpen: () => void; upload: (file: File) => void }) {
  const { data, act } = useAppState();
  const input = useRef<HTMLInputElement>(null);
  // Файл за Claude тоже заливает человек: producer не ограничивает загрузку,
  // достаточно производственной роли (admin/motion) и состояния production.
  const canUpload = ["admin", "user"].includes(data.user.role) && creative.state === "production";
  // «В live» — единственная точка, из которой крео уезжает в Media Library.
  const canLaunch = ["admin", "user"].includes(data.user.role) && creative.state === "ready_for_upload";
  // Кто снимается — видно прямо на карточке: кастинг такая же ось теста, как
  // хук или формат, и открывать ради него карточку не нужно.
  const cast = family.characters.filter((character) => character.id === creative.characterId);
  const spinning = creative.state === "live" || creative.state === "paused_manual";
  const measured = creative.metrics.spend > 0;
  return <article className={`creative-card ${sibling ? "creative-card--sibling" : ""}`} onClick={onOpen}>
    <div className="creative-card__thumb"><CreativeVisual creative={creative} compact /></div>
    <div className="creative-card__main">
      <div className="creative-card__id"><strong>{creativeTitle(creative)}</strong><span>{creative.week ? `W${creative.week}` : ""}</span></div>
      <div className="creative-card__meta">
        {family.author && <span className="author-chip" title="Автор гипотезы"><i>{family.author[0]}</i>{family.author}</span>}
        {/* Продукт показываем, только пока их в справочнике больше одного:
            чип «Lovmia» на каждой карточке единственного продукта — шум. */}
        {family.product && data.settings.products.length > 1 && <span className="product-chip" title="Продукт">{family.product}</span>}
        <b className={`producer producer--${creative.producer}`}>{creative.producer}</b>
        {creative.mlStatus && <span className={`ml-chip ml-chip--${creative.mlStatus}`} title="Статус в Media Library кабинета">{mediaLibraryStatusLabels[creative.mlStatus]}</span>}
        <DeliveryChip creative={creative} />
        <ModerationChip moderation={creative.moderation} thresholds={data.settings.moderation} />
        <PlacementBadge assets={creative.placementAssets} />
        <CastingStrip characters={cast} limit={1} />
      </div>
      {/* Флажки — режим «локали строкой внутри крео». Когда локали стоят на
          доске карточками, тот же факт дважды на одной карточке — шум. */}
      {!data.settings.flatLocalizations && <LocaleFlags creative={creative} />}
      {/* Метрики самого исполнения; прогресс к гейту — в шапке семьи.
          Ряд рендерится ВСЕГДА (пустые значения — «—»): карточки одной колонки
          держат одну высоту, и глаз сравнивает числа по одним координатам. */}
      <CreativeStats creative={creative} />
      {!measured && spinning && !creative.deliveryStatus && <div className="spend-mini"><span>в кабинете · не запущено</span></div>}
      {(canUpload || canLaunch) && <div className="creative-card__quick" onClick={(event) => event.stopPropagation()}>
        {canUpload && <><input ref={input} hidden type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp" onChange={(event) => event.target.files?.[0] && upload(event.target.files[0])} /><button onClick={() => input.current?.click()}><Upload size={13} /> Загрузить</button></>}
        {canLaunch && <button onClick={() => act(`/api/creatives/${creative.id}/mark-live`, { method: "POST" }, `${creative.id} ушло в Media Library`)}><Radio size={13} /> В live</button>}
      </div>}
    </div>
    {/* Быстрые действия прямо с доски — то же меню, что в модалке крео.
        Обёртка гасит клик: диалоги меню рендерятся порталом, но в React-дереве
        всплывают в кликабельный article. */}
    <span className="creative-card__menu" onClick={(event) => event.stopPropagation()}>
      <CreativeControls creative={creative} family={family} />
    </span>
  </article>;
}

/**
 * Карточка локали на доске: та же единица движения, что крео, но на ступень
 * уже. Локаль — отдельное объявление в кабинете (своё имя, свой файл, свой
 * реджект и свои деньги), но не отдельное измерение гипотезы: ни вердикта, ни
 * окраски порогами здесь нет — пороги посчитаны по EN, и судить ими другое гео
 * нечестно. Открывается карточка родителя: разбор локали живёт там.
 */
function LocalizationCard({ item, creative, onOpen }: { item: CreativeLocalization; creative: Creative; onOpen: () => void }) {
  const { data, act, notify, refresh } = useAppState();
  const canAct = ["admin", "user"].includes(data.user.role);
  const isAdmin = data.user.role === "admin";
  const [busy, setBusy] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const upload = async (file: File) => {
    setBusy("upload");
    try { await uploadLocalizationVideo(item.id, file); await refresh(); notify(`${item.id}: файл загружен`, undefined, "success"); }
    catch (error) { notify(error instanceof Error ? error.message : "Не удалось загрузить файл локали", undefined, "danger"); }
    finally { setBusy(null); }
  };
  const call = async (action: "mark-live" | "recall", success: string) => {
    setBusy(action);
    try { await act(`/api/localizations/${item.id}/${action}`, { method: "POST" }, success); }
    catch (error) { notify(error instanceof ApiError ? error.message : "Не получилось", undefined, "danger"); }
    finally { setBusy(null); }
  };
  const canUpload = canAct && item.state !== "live";
  const canLaunch = canAct && item.state === "ready_for_upload" && item.file != null;
  const canRecall = isAdmin && item.state === "live" && item.metrics.spend === 0;
  return <article
    className={`localization-card ${moderationBad(item.moderation, data.settings.moderation) ? "localization-card--rejected" : ""}`}
    onClick={onOpen}
    title={`${localeLabels[item.locale] ?? item.locale} · ${item.adName}`}
  >
    {/* Превью — файл самой локали: в кабинет уезжает он, а не ролик родителя. */}
    <div className="creative-card__thumb"><CreativeVisual creative={{ ...creative, id: item.id, file: item.file }} compact /></div>
    <div className="localization-card__main">
      <div className="localization-card__id">
        <span className={`loc-flag loc-flag--${item.state}`}>{localizationTag(item)}</span>
        <strong>{localeLabels[item.locale] ?? item.locale}</strong>
        <span className="mono">{item.id}</span>
      </div>
      <div className="localization-card__meta">
        <span className="loc-state">{localizationStateLabels[item.state]}</span>
        {item.mlStatus && <span className={`ml-chip ml-chip--${item.mlStatus}`} title="Статус в Media Library кабинета">{mediaLibraryStatusLabels[item.mlStatus]}</span>}
        <DeliveryChip creative={item} />
        <ModerationChip moderation={item.moderation} thresholds={data.settings.moderation} />
        <PlacementBadge assets={item.placementAssets} />
        {item.state === "live" && !item.deliveryStatus && item.metrics.spend === 0 && <em>в кабинете · не запущено</em>}
        {(item.registrationError || item.mediaError) && <TriangleAlert size={12} className="loc-row__warn" />}
      </div>
      {/* Цифры показываем, только когда они есть: у локали нет вердикта, и
          пустой ряд «—» читался бы как измеренный ноль. */}
      {item.metrics.spend > 0 && <div className="stat-row stat-row--locale" title="Своё гео: эти деньги в гейт гипотезы не входят">
        <span><small>hook</small><b>{formatPercent(item.metrics.hookRate)}</b></span>
        <span><small>hold</small><b>{formatPercent(item.metrics.holdRate)}</b></span>
        <span><small>spend</small><b>{formatMoney(item.metrics.spend)}</b></span>
        <span><small>cps</small><b className={item.metrics.cpa == null ? "stat-zero" : ""}>{formatMoney(item.metrics.cpa, 2)}</b></span>
      </div>}
      {(canUpload || canLaunch || canRecall) && <div className="creative-card__quick" onClick={(event) => event.stopPropagation()}>
        {canUpload && <>
          <input ref={input} hidden type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp" onChange={(event) => event.target.files?.[0] && void upload(event.target.files[0])} />
          <button disabled={busy != null} onClick={() => input.current?.click()}><Upload size={13} /> {item.file ? "Заменить" : "Загрузить"}</button>
        </>}
        {canLaunch && <button disabled={busy != null} onClick={() => void call("mark-live", `${item.id} ушла в Media Library`)}><Radio size={13} /> В live</button>}
        {canRecall && <button disabled={busy != null} onClick={() => void call("recall", `${item.id} отозвана`)}><Undo2 size={13} /> Отозвать</button>}
      </div>}
    </div>
  </article>;
}

/**
 * Корешок родителя: локаль обогнала крео по доске (ES-MX ещё делают, EN уже
 * крутится), и карточки крео в этой колонке нет. Строка говорит, чья это
 * локаль и где стоит оригинал — иначе карточка висела бы в колонке сиротой.
 */
function ParentStub({ creative, onOpen }: { creative: Creative; onOpen: () => void }) {
  return <button className="parent-stub" onClick={onOpen} title={`${creative.id} стоит в колонке «${creativeStateLabels[creative.state]}»`}>
    <CornerDownRight size={12} />
    <span className="mono">{creative.id}</span>
    <span>EN · {creativeStateLabels[creative.state]}</span>
  </button>;
}

/**
 * Тамб строки таблицы. Постер генерится сервером и кэшируется; если его нет
 * (старый деплой, нет ffmpeg), картинка честно падает обратно на <video>.
 */
function TableThumb({ file }: { file: Creative["file"] }) {
  if (!file) return <span className="board-table__thumb"><Film size={12} /></span>;
  return <span className="board-table__thumb">
    <img
      src={`/api/files/${file.id}/poster`}
      alt=""
      loading="lazy"
      onError={(event) => {
        const img = event.currentTarget;
        const video = document.createElement("video");
        video.src = `${file.url}#t=0.1`;
        video.muted = true; video.playsInline = true; video.preload = "metadata";
        img.replaceWith(video);
      }}
    />
  </span>;
}

/** Карточка гипотезы для вида «Гипотезы»: Title / Subtitle / Description + теги. */
function HypothesisCard({ family, onOpen }: { family: Hypothesis; onOpen: () => void }) {
  const { data } = useAppState();
  // Линия эволюции: из чего выросла и что выросло из неё. Карточка сама кнопка,
  // поэтому здесь это подпись, а не ссылка — переход по линии живёт в модалке.
  const variations = data.hypotheses.filter((item) => item.parentId === family.id);
  const spend = family.creatives.reduce((sum, creative) => sum + creative.metrics.spend, 0);
  const measured = family.creatives.filter((creative) => creative.metrics.spend > 0);
  const bestHook = measured.length ? Math.max(...measured.map((creative) => creative.metrics.hookRate)) : null;
  const cpas = measured.map((creative) => creative.metrics.cpa).filter((value): value is number => value != null);
  // Меню — сосед карточки, а не её содержимое: карточка сама кнопка.
  return <div className="concept-card-wrap">
    <HypothesisControls family={family} className="menu-trigger--float" />
    <button className="concept-card" onClick={onOpen}>
      <div className="concept-card__top">
        <span className="mono">{family.id}</span>
        {family.parentId && <span className="lineage-chip" title={family.variationNote ?? `Вариация ${family.parentId}`}>
          <GitBranch size={11} />{family.parentId}
        </span>}
        <StatusPill status={family.status} />
        <span className="concept-card__kpi">KPI · {family.judge}</span>
      </div>
      <strong className="concept-card__title">{family.title ?? "Без имени механики"}</strong>
      <p className="concept-card__subtitle">{family.statement}</p>
      {family.description && <p className="concept-card__description">{family.description}</p>}
      <div className="concept-card__tags">
        <Tag tone="pain">{family.pain}</Tag>
        <Tag tone="desire">{family.desire}</Tag>
        <Tag>{family.angle}</Tag>
        {family.tone && <Tag tone="accent">{family.tone}</Tag>}
      </div>
      <CastingStrip characters={family.characters} />
      <div className="concept-card__foot">
        <span>{family.creatives.length ? `${family.creatives.length} вариаций · ${formatMoney(spend)}${bestHook != null ? ` · hook ${formatPercent(bestHook)}` : ""}${cpas.length ? ` · CPS ${formatMoney(Math.min(...cpas), 2)}` : ""}` : "крео ещё нет"}</span>
        <em>{variations.length ? `${variations.length} вариаци${variations.length === 1 ? "я" : "и"} · ` : ""}{family.source}{family.batch ? ` · ${family.batch}` : ""}</em>
      </div>
    </button>
  </div>;
}

function CreateHypothesis({ close }: { close: () => void }) {
  const { data, act, notify } = useAppState();
  const { upload: uploadVideo } = useCreativeUpload();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ statement: "", pain: data.taxonomy.pain[0], desire: data.taxonomy.desire[0], angle: data.taxonomy.angle[0], source: "team", priority: "P2", judge: "cpa", author: "Andrey", product: data.settings.products[0] ?? "Lovmia" });
  // Готовый ролик можно приаттачить прямо при создании: гипотеза появится на
  // доске сразу с C1 и файлом. В Media Library при этом ничего не уезжает —
  // пуш только по кнопке «В live».
  const [video, setVideo] = useState<File | null>(null);
  const [producer, setProducer] = useState("motion");
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setLoading(true);
    try {
      const hypothesis = await act<Hypothesis>("/api/hypotheses", { method: "POST", body: JSON.stringify(form) }, "Гипотеза добавлена в бэклог");
      if (video) {
        const family = await act<Hypothesis>(`/api/hypotheses/${hypothesis.id}/creatives`, { method: "POST", body: JSON.stringify({ producer }) });
        const creativeId = family.creatives[family.creatives.length - 1]?.id;
        if (creativeId) await uploadVideo(creativeId, video);
        else notify("Крео не создалось — загрузите файл из карточки", undefined, "danger");
      }
      close();
    }
    finally { setLoading(false); }
  };
  return <Modal onClose={close}><form className="dialog-form" onSubmit={submit}>
    <span className="eyebrow">Новая проверка</span><h2>Зафиксировать гипотезу</h2><p>Одно утверждение, одна причинная связь и один главный показатель.</p>
    <label className="field field--full"><span>Утверждение</span><textarea autoFocus required minLength={8} value={form.statement} onChange={(event) => setForm({ ...form, statement: event.target.value })} placeholder="Если показать…, то…, потому что…" /></label>
    <div className="form-grid">
      {(["pain", "desire", "angle"] as const).map((key) => <label className="field" key={key}><span>{key}</span><Select value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })}>{data.taxonomy[key].map((item) => <option key={item}>{item}</option>)}</Select></label>)}
      <label className="field"><span>Приоритет</span><Select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}>{["P0", "P1", "P2", "P3"].map((item) => <option key={item}>{item}</option>)}</Select></label>
      <label className="field"><span>Судим по</span><Select value={form.judge} onChange={(event) => setForm({ ...form, judge: event.target.value })}>{["cpa", "hook_rate", "hold_rate", "ctr"].map((item) => <option key={item}>{item}</option>)}</Select></label>
      <label className="field"><span>Автор</span><Select value={form.author} onChange={(event) => setForm({ ...form, author: event.target.value })}>
        <option value="Andrey">Andrey</option>
        <option value="Julia">Julia</option>
      </Select></label>
      {/* Единственный продукт не спрашиваем — уйдёт дефолтом формы. */}
      {data.settings.products.length > 1 && <label className="field"><span>Продукт</span><Select value={form.product} onChange={(event) => setForm({ ...form, product: event.target.value })}>
        {data.settings.products.map((item) => <option key={item}>{item}</option>)}
      </Select></label>}
    </div>
    <div className="form-grid">
      <label className="field"><span>Файл C1 — видео или картинка (опц.)</span><input type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp" onChange={(event) => setVideo(event.target.files?.[0] ?? null)} /></label>
      {video && <label className="field"><span>Producer</span><Select value={producer} onChange={(event) => setProducer(event.target.value)}>
        <option value="motion">Motion</option>
        <option value="julia">Julia</option>
        <option value="claude">Claude</option>
      </Select></label>}
    </div>
    <div className="dialog-actions"><Button type="button" variant="ghost" onClick={close}>Отмена</Button><Button type="submit" variant="primary" loading={loading}>{video ? "Создать и приаттачить" : "Создать"}</Button></div>
  </form></Modal>;
}

/** Шаблон свободным текстом; для старых записей собирается из masterScript JSON. */
function templateFallback(family: Hypothesis) {
  if (family.template) return family.template;
  const script = family.masterScript;
  if (!script) return "";
  const beats = script.beats.map((beat) =>
    `${beat.t} (акт ${beat.act}) — ${beat.beat}` +
    (beat.meaning ? `\n  Смысл: ${beat.meaning}` : "") +
    (beat.overlay ? `\n  Overlay: ${beat.overlay}` : "")).join("\n");
  const locks = script.locks.length ? `\n\nЛоки: ${script.locks.join(" · ")}` : "";
  return `Арка: ${script.arc}\n\n${beats}${locks}`;
}

/** Тело крео свободным текстом; для старых записей собирается из слотов. */
function scriptFallback(creative: Creative) {
  if (creative.script) return creative.script;
  if (!creative.slots) return "";
  return Object.entries(creative.slots).map(([slot, value]) => `${slot.replaceAll("_", " ")}: ${value}`).join("\n");
}

/**
 * Поля, которые читает только модалка. Облегчённый bootstrap их не несёт
 * (null или нет вовсе), поэтому при `slim` они всегда берутся из детального
 * ответа, а всё остальное — из bootstrap: его обновляет живая лента.
 */
const creativeDetailFields = ["script", "slots", "comment", "urlTags", "character", "adName", "diagnosis"] as const satisfies ReadonlyArray<keyof Creative>;
const hypothesisDetailFields = ["template", "masterScript", "sourceVideo"] as const satisfies ReadonlyArray<keyof Hypothesis>;

function withDetail<T extends object, K extends keyof T>(base: T, detail: T | undefined, fields: readonly K[]): T {
  if (!detail) return base;
  const merged = { ...base };
  for (const field of fields) merged[field] = detail[field];
  return merged;
}

/** Место под сценарий или шаблон, пока детали в пути; без модальных алертов. */
function DetailLoading({ failed, retry }: { failed: boolean; retry: () => void }) {
  return <div className="detail-loading">
    {failed
      ? <><TriangleAlert size={14} />Детали не загрузились<button onClick={retry}>Повторить</button></>
      : <><LoaderCircle size={14} className="spin" />Загружаем детали</>}
  </div>;
}

/** Открытая карточка вариации: гипотеза, теги, свободный сценарий, действия. */
function CreativeModal({ creative: boardCreative, family, close }: { creative: Creative; family: Hypothesis; close: () => void }) {
  const { data, act, notify } = useAppState();
  const navigate = useNavigate();
  // Облегчённая доска: сценарий, комментарий и разметка — из детального ответа.
  // Старый сервер (`slim` не пришёл) — всё уже в bootstrap, запроса нет.
  const slim = isSlimBootstrap(data);
  const detailQuery = useCreativeDetail(boardCreative.id, slim);
  const detailReady = !slim || detailQuery.data != null;
  const detailPending = <DetailLoading failed={detailQuery.isError} retry={() => void detailQuery.refetch()} />;
  const creative = slim ? withDetail(boardCreative, detailQuery.data, creativeDetailFields) : boardCreative;
  const [busy, setBusy] = useState<string | null>(null);
  // Черновик — null, пока его не трогали: до правки поле показывает
  // сохранённое и подхватывает детали, когда они доедут, а не затирает их пустым.
  const savedScript = scriptFallback(creative);
  const [scriptEdit, setScriptDraft] = useState<string | null>(null);
  const scriptDraft = scriptEdit ?? savedScript;
  const canEditScript = ["admin", "user"].includes(data.user.role);
  const scriptDirty = scriptEdit != null && scriptEdit !== savedScript;
  const saveScript = async () => {
    setBusy("script");
    try { await act(`/api/creatives/${creative.id}/variation`, { method: "PATCH", body: JSON.stringify({ script: scriptDraft.trim() || null }) }, "Сценарий сохранён"); }
    finally { setBusy(null); }
  };
  const copyScript = () => {
    navigator.clipboard.writeText(scriptDraft);
    notify("Сценарий скопирован");
  };
  const fileInput = useRef<HTMLInputElement>(null);
  const canUpload = ["admin", "user"].includes(data.user.role) && creative.state === "production" && !creative.file;
  const { upload: uploadVideo, label: uploadLabel } = useCreativeUpload();
  const uploadFile = async (file: File) => {
    setBusy("upload");
    try { await uploadVideo(creative.id, file); }
    finally { setBusy(null); }
  };
  // Ad-name и разметка нужны только UA при заливе — живут в «⋯», не в теле.
  const [menuOpen, setMenuOpen] = useState(false);
  const canAct = ["admin", "user"].includes(data.user.role);
  // Комментарий по крео: замечания и контекст, бывшая «переделка» — теперь
  // просто текст на карточке, движение по доске он не двигает.
  const savedComment = creative.comment ?? "";
  const [commentEdit, setCommentDraft] = useState<string | null>(null);
  const commentDraft = commentEdit ?? savedComment;
  const commentDirty = commentEdit != null && commentEdit !== savedComment;
  const saveComment = async () => {
    setBusy("comment");
    try { await act(`/api/creatives/${creative.id}/variation`, { method: "PATCH", body: JSON.stringify({ comment: commentDraft.trim() || null }) }, "Комментарий сохранён"); }
    catch (error) { notify(error instanceof ApiError ? error.message : "Не удалось сохранить комментарий", undefined, "danger"); }
    finally { setBusy(null); }
  };
  // Решения по вердикту — рабочие действия любого оператора: кто нажал, видно
  // в ленте событий по актору.
  const [reworkOpen, setReworkOpen] = useState(false);
  const [reworkNote, setReworkNote] = useState("");
  const decision = async (action: "scale" | "lose" | "test2" | "iterate" | "archive", comment?: string) => {
    setBusy(action);
    const note = action === "scale" ? "Отправлено в виннеры"
      : action === "lose" ? "Отправлено в лузеры"
      : action === "test2" ? "Крео ушло в тест 2 — запускайте скейл-прогон"
        : action === "iterate" ? "Создана новая версия" : "Отправлено в архив";
    try { await act(`/api/creatives/${creative.id}/decision`, { method: "POST", body: JSON.stringify({ action, ...(comment ? { comment } : {}) }) }, note); }
    finally { setBusy(null); }
  };
  const zeroSpend = creative.metrics.spend <= 0;
  // Producer правится прямо в карточке. Что при этом станет с именем
  // объявления, решает сервер по следу крео; здесь — только честный тост.
  const changeProducer = async (producer: string) => {
    if (producer === creative.producer) return;
    setBusy("producer");
    const footprint = creativeFootprint(creative);
    const note = footprint.measured
      ? "Producer изменён — только подпись: имя объявления уже в кабинете и не меняется"
      : footprint.registered
        ? "Producer изменён, имя пересобрано. Крео отозвано из Media Library — после проверки нажмите «В live»"
        : "Producer изменён, имя объявления пересобрано";
    try { await act(`/api/creatives/${creative.id}/variation`, { method: "PATCH", body: JSON.stringify({ producer }) }, note); }
    catch (error) { notify(error instanceof ApiError ? error.message : "Не удалось сменить producer", undefined, "danger"); }
    finally { setBusy(null); }
  };
  // Минимальный шаблон (утверждён макетом): имя → статус → видео | стадка +
  // гипотеза + следующий статус → сценарий. Ad-name и разметка — в «⋯».
  return <Modal onClose={close}><div className="creative-modal">
    <header className="creative-modal__header">
      <span className="creative-modal__eyebrow mono">{family.id} · C{creative.id.match(/-C(\d+)/)?.[1] ?? "?"}{creative.week ? ` · W${creative.week}` : ""}</span>
      <h2 className="creative-modal__title">{creative.variationTitle ?? creative.id}</h2>
      <div className="cm-status">
        {creative.state === "verdict_pending" || creative.verdict ? <VerdictPill verdict={creative.verdict} /> : <span className={`creative-state creative-state--${creative.state}`}>{creativeStateLabels[creative.state]}</span>}
        <DeliveryChip creative={creative} />
        <button className="cm-more" aria-label="Данные для кабинета" title="Имя объявления и разметка для UA" onClick={() => setMenuOpen((value) => !value)}>ad_name</button>
        {/* Перенести, отделить, удалить — видимое меню, а не секрет под «⋯». */}
        <CreativeControls creative={creative} family={family} onDone={close} />
      </div>
      {menuOpen && <div className="cm-menu">
        {detailReady ? <>
          <div className="ad-name"><span>{creative.adName}</span><button onClick={() => { navigator.clipboard.writeText(creative.adName); notify("Имя скопировано"); }}><Copy size={13} /></button></div>
          <AdTracking creative={creative} />
        </> : detailPending}
      </div>}
    </header>
    <div className="cm-main">
      <div className="cm-video">
        {canUpload ? <button className="upload-drop" onClick={() => fileInput.current?.click()} disabled={busy === "upload"} aria-label="Загрузить файл крео">
          <input ref={fileInput} hidden type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp" onChange={(event) => event.target.files?.[0] && uploadFile(event.target.files[0])} />
          <CreativeVisual creative={creative} />
          <span className="upload-drop__hint"><CloudUpload size={15} /> {busy === "upload" ? uploadLabel ?? "Загружаем…" : "Загрузить файл"}</span>
        </button> : <CreativeVisual creative={creative} controls />}
      </div>
      <div className="cm-side">
        <div className="cm-mbig">
          <div><span>Hook</span><b className={creative.metrics.hookRate <= 0 ? "zero" : creative.metrics.hookRate >= data.settings.hookRateMin ? "good" : "bad"}>{formatPercent(creative.metrics.hookRate)}</b></div>
          <div><span>Hold</span><b className={creative.metrics.holdRate <= 0 ? "zero" : creative.metrics.holdRate >= data.settings.holdRateMin ? "good" : "bad"}>{formatPercent(creative.metrics.holdRate)}</b></div>
          <div><span>CTR</span><b className={creative.metrics.ctr <= 0 ? "zero" : ""}>{formatPercent(creative.metrics.ctr, 2)}</b></div>
        </div>
        <div className="cm-msmall">
          <span>Spend<b className={zeroSpend ? "zero" : ""} title={creative.test2StartSpend != null ? "суммарно за обе фазы" : undefined}>{formatMoney(creative.metrics.spend)}</b></span>
          <span>CPS<b className={creative.metrics.cpa == null ? "zero" : ""}>{formatMoney(creative.metrics.cpa, 2)}</b></span>
          <span>Producer{canAct
            ? <Select value={creative.producer} disabled={busy === "producer"} aria-label="Кто собрал крео" onChange={(event) => void changeProducer(event.target.value)}>
              {producers.map((producer) => <option key={producer} value={producer}>{producer}</option>)}
            </Select>
            : <b>{creative.producer}</b>}</span>
        </div>
        {creative.test2StartSpend != null && <div className="family-facts mono">
          тест 1 {formatMoney(creative.test2StartSpend)} · тест 2 {formatMoney(Math.max(0, creative.metrics.spend - creative.test2StartSpend))} / {formatMoney(data.settings.test2Spend)} · всего {formatMoney(creative.metrics.spend)}
        </div>}
        <DeliveryNote creative={creative} />
        <button className="hyp-card" onClick={() => navigate(`/hypotheses/${family.id}`, { preserveScroll: true })}>
          <span className="hyp-card__in">
            <span className="hyp-card__head">
              <span className="hyp-card__eyebrow">Гипотеза</span>
              <span className="hyp-card__id mono">{family.id}</span>
              <ArrowUpRight className="hyp-card__arr" size={15} />
            </span>
            <span className="hyp-card__name">{family.title ?? family.statement}</span>
            <span className="hyp-card__tags"><i>{family.pain}</i><i>{family.desire}</i><i>{family.angle}</i>{family.tone && <i>{family.tone}</i>}</span>
          </span>
        </button>
        <div className="cm-advance">
          {/* Контекстная кнопка следующего статуса вместо россыпи действий. */}
          {canAct && creative.state === "ready_for_upload" && <>
            <Button variant="primary" onClick={() => act(`/api/creatives/${creative.id}/mark-live`, { method: "POST" }, `${creative.id} ушло в Media Library`)}><Radio size={15} /> В live</Button>
            <small>файл загружен · кнопка отправит крео в Media Library кабинета</small>
          </>}
          {canAct && creative.state === "production" && <small>{creative.file ? "файл на месте — статус обновится сам" : "нажми на превью, чтобы загрузить файл"}</small>}
          {canAct && creative.state === "live" && <>
            <Button onClick={() => act(`/api/creatives/${creative.id}/pause`, { method: "POST", body: JSON.stringify({ reason: "Остановлено после ручной проверки UA" }) }, "Открутка остановлена")}><CirclePause size={15} /> Стоп</Button>
            {/* Пока UA не запустил рекламу (нет спенда), случайный «В live» можно откатить целиком. */}
            {data.user.role === "admin" && creative.metrics.spend === 0 && <Button variant="ghost" onClick={() => act(`/api/creatives/${creative.id}/recall`, { method: "POST" }, `${creative.id} отозвано из Media Library`)}><Undo2 size={15} /> Отозвать</Button>}
            <small>{creative.metrics.spend === 0 ? "ушло в Media Library · «Отозвать» вернёт на доску для правок" : "крутится · набирает бюджет на вердикт"}</small>
          </>}
          {canAct && (creative.state === "verdict_pending" || creative.state === "verdict_pending_test2") && <div className="cm-decide">
            {/* «В виннеры» доступна из вердикта обеих фаз — фазу можно перепрыгнуть. */}
            <Button variant="primary" loading={busy === "scale"} onClick={() => decision("scale")}><ArrowUpRight size={15} /> Отправить в виннеры</Button>
            <Button variant="danger" loading={busy === "lose"} onClick={() => decision("lose")}><ArrowDownRight size={15} /> В лузеры</Button>
            {creative.state === "verdict_pending" && <Button loading={busy === "test2"} onClick={() => decision("test2")}><ArrowRight size={15} /> Перенести в тест 2</Button>}
            <Button loading={busy === "iterate"} onClick={() => setReworkOpen(true)}><RotateCcw size={15} /> Переделать</Button>
            <Button variant="ghost" loading={busy === "archive"} onClick={() => decision("archive")}>Отправить в архив</Button>
          </div>}
          {creative.state === "live_test2" && <small>скейл-прогон в боевой кампании · копит бюджет теста 2{creative.test2StartedAt ? ` · с ${formatDate(creative.test2StartedAt)}` : ""}</small>}
          {creative.state === "scaled" && <small>победитель · бюджет льём сюда</small>}
          {creative.state === "lost" && <small>лузер · тест провален, бюджет не льём</small>}
        </div>
      </div>
    </div>
    <LocalizationsSection creative={creative} />
    <p className="section-label">Сценарий</p>
    <div className="modal-card">
      <div className="script-head">
        <span>Свободный текст: биты, реплики, тайминги</span>
        <div className="script-head__actions">
          {detailReady && scriptDraft && <Button onClick={copyScript}><Copy size={14} /> Копировать</Button>}
          {canEditScript && scriptDirty && <Button variant="primary" loading={busy === "script"} onClick={saveScript}>Сохранить</Button>}
        </div>
      </div>
      {!detailReady ? detailPending : canEditScript ? <textarea
        className="script-editor"
        value={scriptDraft}
        onChange={(event) => setScriptDraft(event.target.value)}
        placeholder={"Биты, реплики, тайминги — структура своя у каждого крео.\nНапример:\nГэг-открывашка 0–2s — коза орёт в камеру, она невозмутима [keep_tail]\nРеплика-запрет 3–8s — \u201cStop scrolling. Do NOT download this app.\u201d"}
      /> : scriptDraft ? <pre className="script-view">{scriptDraft}</pre> : <p className="script-empty">Сценария пока нет. Попроси Claude в чате: «напиши сценарий для {creative.id}» — он сохранит его сюда через set_variation.</p>}
    </div>
    <p className="section-label">Комментарий</p>
    <div className="modal-card">
      <div className="script-head">
        <span>Замечания по крео: что исправить, контекст для команды</span>
        <div className="script-head__actions">
          {canAct && commentDirty && <Button variant="primary" loading={busy === "comment"} onClick={saveComment}>Сохранить</Button>}
        </div>
      </div>
      {!detailReady ? detailPending : canAct ? <textarea
        className="script-editor script-editor--comment"
        value={commentDraft}
        onChange={(event) => setCommentDraft(event.target.value)}
        placeholder="Переснять концовку: реплика тонет в музыке, последние 2 секунды без лица"
      /> : commentDraft ? <pre className="script-view">{commentDraft}</pre> : <p className="script-empty">Комментария пока нет.</p>}
    </div>
    {creative.diagnosis && <div className="diagnosis-note"><Sparkles size={14} /><p>{creative.diagnosis}</p></div>}
    {reworkOpen && <Modal onClose={() => setReworkOpen(false)}><form className="dialog-form" onSubmit={(event) => { event.preventDefault(); setReworkOpen(false); void decision("iterate", reworkNote.trim()); }}>
      <span className="eyebrow">Переделать</span><h2>Новая версия {creative.id}</h2>
      <p>Замечание обязательно: оно станет ТЗ на карточке новой версии.</p>
      <label className="field field--full"><span>Что переделать</span><textarea autoFocus required minLength={3} maxLength={500} value={reworkNote} onChange={(event) => setReworkNote(event.target.value)} placeholder="Переснять концовку: реплика тонет в музыке, последние 2 секунды без лица" /></label>
      <div className="dialog-actions"><Button type="button" variant="ghost" onClick={() => setReworkOpen(false)}>Отмена</Button><Button type="submit" variant="primary" loading={busy === "iterate"}>Создать версию</Button></div>
    </form></Modal>}
  </div></Modal>;
}

/** Флажки локалей на карточке: `ES-MX · PT-BR`, цвет — по состоянию и слову кабинета. */
function LocaleFlags({ creative }: { creative: Creative }) {
  const { data } = useAppState();
  if (!creative.localizations.length) return null;
  return <div className="loc-flags" title="Локализации этого крео">
    <Languages size={11} />
    {creative.localizations.map((item) => <span
      key={item.id}
      className={`loc-flag loc-flag--${item.state} ${moderationBad(item.moderation, data.settings.moderation) ? "loc-flag--rejected" : ""}`}
      title={`${localeLabels[item.locale] ?? item.locale} · ${localizationStateLabels[item.state]}${moderationTitle(item.moderation, data.settings.moderation)}${item.metrics.spend > 0 ? ` · ${formatMoney(item.metrics.spend)}` : ""}`}
    >{localizationTag(item)}</span>)}
  </div>;
}

/** «· реджекты 2/9 · петушиный угол» для подсказок; пусто, пока фид молчит. */
function moderationTitle(moderation: Moderation | null, thresholds: WorkspaceSettings["moderation"]) {
  if (!moderation) return "";
  const status = moderationVerdict(moderation, thresholds).status;
  return ` · реджекты ${moderation.rejected}/${moderation.launched}${status ? ` · ${moderationStatusLabels[status]}` : ""}`;
}

/** Локаль из имени файла: `H-016-C3_ES.mp4` → es_*, `…__pt_BR.mp4` → pt_BR. Регион не угадываем без нужды — первая локаль языка из списка кабинета. */
function localeFromFilename(name: string, taken: Set<string>): string | null {
  const exact = name.match(/[_-]([a-z]{2})[_-]([A-Z]{2})\.[A-Za-z0-9]+$/);
  if (exact) {
    const locale = `${exact[1]}_${exact[2]}`;
    if ((supportedLocales as readonly string[]).includes(locale) && !taken.has(locale)) return locale;
  }
  const language = name.match(/[_-]([A-Za-z]{2})\.[A-Za-z0-9]+$/)?.[1]?.toLowerCase();
  if (!language) return null;
  return supportedLocales.find((locale) => locale.startsWith(`${language}_`) && !taken.has(locale)) ?? null;
}

/**
 * Локализации крео: тот же ролик на другом языке для другого гео. Строка внутри
 * крео, но отдельное объявление в кабинете — со своим «В live», отзывом,
 * реджектом и цифрами. Сюда же «добавь локаль» из чата: Claude через
 * add_localization кладёт строку с файлом, человек отправляет её кнопкой.
 */
function LocalizationsSection({ creative }: { creative: Creative }) {
  const { data, act, notify, refresh } = useAppState();
  const canAct = ["admin", "user"].includes(data.user.role);
  const isAdmin = data.user.role === "admin";
  const [busy, setBusy] = useState<string | null>(null);
  const taken = new Set(creative.localizations.map((item) => item.locale));
  const available = supportedLocales.filter((locale) => locale !== "en_US" && !taken.has(locale));
  const [locale, setLocale] = useState<string>(available[0] ?? "");
  const [pending, setPending] = useState<File | null>(null);
  const [remove, setRemove] = useState<CreativeLocalization | null>(null);
  const [redo, setRedo] = useState<CreativeLocalization | null>(null);
  const [redoNote, setRedoNote] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadInputs = useRef<Record<string, HTMLInputElement | null>>({});
  if (!canAct && !creative.localizations.length && !creative.placementAssets.length) return null;

  const pick = (file: File | null) => {
    setPending(file);
    if (!file) return;
    const guess = localeFromFilename(file.name, taken);
    if (guess) setLocale(guess);
  };
  const upload = async (localizationId: string, file: File) => {
    setBusy(`upload-${localizationId}`);
    try { await uploadLocalizationVideo(localizationId, file); await refresh(); notify(`${localizationId}: файл загружен`, undefined, "success"); }
    catch (error) { notify(error instanceof Error ? error.message : "Не удалось загрузить файл локали", undefined, "danger"); }
    finally { setBusy(null); }
  };
  const add = async () => {
    if (!locale) return;
    setBusy("add");
    try {
      const item = await act<CreativeLocalization>(`/api/creatives/${creative.id}/localizations`, { method: "POST", body: JSON.stringify({ locale }) });
      if (pending) await upload(item.id, pending);
      else notify(`${item.id} добавлена — загрузите файл`, undefined, "success");
      setPending(null);
      const next = available.filter((entry) => entry !== locale);
      setLocale(next[0] ?? "");
    } catch (error) { notify(error instanceof ApiError ? error.message : "Не удалось добавить локаль", undefined, "danger"); }
    finally { setBusy(null); }
  };
  const call = async (item: CreativeLocalization, action: "mark-live" | "recall", success: string) => {
    setBusy(`${action}-${item.id}`);
    try { await act(`/api/localizations/${item.id}/${action}`, { method: "POST" }, success); }
    catch (error) { notify(error instanceof ApiError ? error.message : "Не получилось", undefined, "danger"); }
    finally { setBusy(null); }
  };
  const addVersion = async (event: FormEvent) => {
    event.preventDefault();
    if (!redo) return;
    setBusy(`version-${redo.id}`);
    try {
      const item = await act<CreativeLocalization>(`/api/localizations/${redo.id}/versions`, { method: "POST", body: JSON.stringify({ note: redoNote.trim() }) });
      notify(`${item.id} добавлена — загрузите файл`, undefined, "success");
      setRedo(null);
      setRedoNote("");
    } catch (error) { notify(error instanceof ApiError ? error.message : "Не удалось создать версию", undefined, "danger"); }
    finally { setBusy(null); }
  };
  const del = async () => {
    if (!remove) return;
    setBusy(`delete-${remove.id}`);
    try { await act(`/api/localizations/${remove.id}${remove.metrics.spend > 0 ? "?force=true" : ""}`, { method: "DELETE" }, `${remove.id} удалена`); setRemove(null); }
    catch (error) { notify(error instanceof ApiError ? error.message : "Не удалось удалить локаль", undefined, "danger"); }
    finally { setBusy(null); }
  };

  return <>
    <p className="section-label">Локализации и форматы</p>
    <div className="modal-card loc-list">
      <div className="loc-row loc-row--base">
        <span className="loc-flag loc-flag--live">EN-US</span>
        <span className="loc-row__title">база · {creativeStateLabels[creative.state]}</span>
        <span className="loc-row__meta">{creative.metrics.spend > 0 ? `${formatMoney(creative.metrics.spend)} · hook ${formatPercent(creative.metrics.hookRate)}` : ""}</span>
        <PlacementStrip subjectId={creative.id} file={creative.file} assets={creative.placementAssets} live={creative.creativeUid != null} />
      </div>
      {creative.localizations.map((item) => <div className={`loc-row ${moderationBad(item.moderation, data.settings.moderation) ? "loc-row--rejected" : ""}`} key={item.id}>
        <span className={`loc-flag loc-flag--${item.state}`}>{localizationTag(item)}</span>
        <span className="loc-row__title">
          <b>{localeLabels[item.locale] ?? item.locale}</b>
          <small className="mono">{item.id}</small>
          {item.versionNote && <small title={`Переделка ${item.parentLocalizationId ?? ""}`}>{item.versionNote}</small>}
        </span>
        <span className="loc-row__meta">
          {item.file ? null : <em>нет файла</em>}
          {item.state === "live" && !item.deliveryStatus && item.metrics.spend === 0 && <em>в кабинете · не запущено</em>}
          {item.state === "ready_for_upload" && <em>готово к заливу</em>}
          <DeliveryChip creative={item} />
          <ModerationChip moderation={item.moderation} thresholds={data.settings.moderation} />
          {item.mlStatus && <span className={`ml-chip ml-chip--${item.mlStatus}`}>{mediaLibraryStatusLabels[item.mlStatus]}</span>}
          {item.metrics.spend > 0 && <span>{formatMoney(item.metrics.spend)} · hook {formatPercent(item.metrics.hookRate)}</span>}
          {(item.registrationError || item.mediaError) && <TriangleAlert size={13} className="loc-row__warn" />}
        </span>
        {canAct && <span className="loc-row__actions">
          {item.file && <a className="loc-row__link" href={item.file.url} target="_blank" rel="noreferrer">файл</a>}
          {item.state !== "live" && <>
            <input hidden type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp" ref={(node) => { uploadInputs.current[item.id] = node; }} onChange={(event) => event.target.files?.[0] && void upload(item.id, event.target.files[0])} />
            <Button loading={busy === `upload-${item.id}`} onClick={() => uploadInputs.current[item.id]?.click()}><Upload size={13} /> {item.file ? "Заменить" : "Загрузить"}</Button>
          </>}
          {item.state === "ready_for_upload" && item.file && <Button variant="primary" loading={busy === `mark-live-${item.id}`} onClick={() => call(item, "mark-live", `${item.id} ушла в Media Library`)}><Radio size={13} /> В live</Button>}
          {isAdmin && item.state === "live" && item.metrics.spend === 0 && <Button variant="ghost" loading={busy === `recall-${item.id}`} onClick={() => call(item, "recall", `${item.id} отозвана`)}><Undo2 size={13} /> Отозвать</Button>}
          {item.file && <Button variant="ghost" title="Переделанный ролик того же языка — отдельное объявление, крутится параллельно" onClick={() => setRedo(item)}><GitBranchPlus size={13} /> Версия</Button>}
          {isAdmin && <button className="loc-row__delete" title="Удалить локаль" disabled={busy != null} onClick={() => setRemove(item)}><Trash2 size={13} /></button>}
        </span>}
        {item.statusDetail && (item.moderation?.rejected ?? 0) > 0 && <p className="loc-row__detail">{item.statusDetail}</p>}
        <PlacementStrip subjectId={item.id} file={item.file} assets={item.placementAssets} live={item.state === "live"} />
      </div>)}
      {canAct && available.length > 0 && <div className="loc-add">
        <Languages size={14} />
        <Select value={locale} onChange={(event) => setLocale(event.target.value)} aria-label="Локаль">
          {available.map((entry) => <option key={entry} value={entry}>{localeTag(entry)} · {localeLabels[entry]}</option>)}
        </Select>
        <input ref={fileInput} hidden type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp" onChange={(event) => pick(event.target.files?.[0] ?? null)} />
        <Button onClick={() => fileInput.current?.click()}><Upload size={13} /> {pending ? pending.name : "Файл (опц.)"}</Button>
        <Button variant="primary" loading={busy === "add"} disabled={!locale} onClick={add}><Plus size={13} /> Добавить локаль</Button>
        <small>Тот же ролик на другом языке — отдельное объявление в кабинете, свои цифры. Или попроси Claude: «добавь локаль {locale || "es_MX"} в {creative.id}».</small>
      </div>}
    </div>
    {redo && <Modal onClose={() => setRedo(null)}><form className="dialog-form" onSubmit={addVersion}>
      <span className="eyebrow">Новая версия локали</span><h2>Переделать {redo.id}</h2>
      <p>Тот же крео и тот же язык, новая сборка. Версия станет отдельным объявлением со своим именем и цифрами и будет крутиться параллельно с {redo.id} — ничего не архивируется.</p>
      {redo.metrics.spend === 0 && redo.state !== "live" && <p className="field-note">{redo.id} ещё не в кабинете — переделку можно просто загрузить в неё кнопкой «Заменить».</p>}
      <label className="field field--full"><span>Что переделали</span><textarea autoFocus required minLength={3} maxLength={500} value={redoNote} onChange={(event) => setRedoNote(event.target.value)} placeholder="Другой транскрайбер: первый глотал окончания, субтитры крупнее" /></label>
      <div className="dialog-actions"><Button type="button" variant="ghost" onClick={() => setRedo(null)}>Отмена</Button><Button type="submit" variant="primary" loading={busy === `version-${redo.id}`}>Создать версию</Button></div>
    </form></Modal>}
    {remove && <ConfirmDialog
      title={`Удалить локаль ${remove.id}?`}
      confirmLabel={remove.metrics.spend > 0 ? "Удалить принудительно" : "Удалить"}
      loading={busy === `delete-${remove.id}`}
      onConfirm={del}
      onClose={() => setRemove(null)}
      description={<>
        <p>{localeLabels[remove.locale] ?? remove.locale}: строка исчезнет с доски, файл в хранилище останется.</p>
        {remove.creativeUid && remove.metrics.spend === 0 && <p>Локаль заведена в кабинете, но не откручивалась — сначала будет отозвана из Media Library.</p>}
        {remove.metrics.spend > 0 && <p className="field-note field-note--danger">Локаль уже открутила {formatMoney(remove.metrics.spend)}: объявление в кабинете останется без владельца, её цифры исчезнут с доски.</p>}
      </>}
    />}
  </>;
}

/** «+1:1» на карточке доски: у объявления есть картинка под ленту — UA её не пропустит. */
function PlacementBadge({ assets }: { assets: PlacementAsset[] }) {
  if (!assets.length) return null;
  return <span className="placement-chip placement-chip--set" title="Картинки под ленту этого же объявления — в карточке, раздел «Локализации и форматы»">
    +{assets.map((asset) => asset.aspect).join(" ")}
  </span>;
}

/** Размер картинки до загрузки: проверка в браузере экономит заливку; решает всё равно сервер. */
async function imageSize(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return null;
  }
}

const aspectRatios: Record<PlacementAspect, number> = { "1:1": 1, "4:5": 4 / 5 };

/**
 * Форматы одного объявления: основной файл (9:16 под Reels/Stories) и
 * картинки под ленту — 1:1, 4:5. Это не новое объявление: имя и цифры общие,
 * UA ставит квадрат в настройку ассета по плейсменту. В Media Library квадрат
 * не уезжает — его скачивают отсюда, файл назван именем объявления.
 */
function PlacementStrip({ subjectId, file, assets, live }: {
  subjectId: string; file: CreativeFile | null; assets: PlacementAsset[]; live: boolean;
}) {
  const { data, act, notify, refresh } = useAppState();
  const canAct = ["admin", "user"].includes(data.user.role);
  const [busy, setBusy] = useState<string | null>(null);
  const inputs = useRef<Partial<Record<PlacementAspect, HTMLInputElement | null>>>({});
  if (!file && !assets.length) return null;
  const byAspect = new Map(assets.map((asset) => [asset.aspect, asset]));

  const upload = async (aspect: PlacementAspect, picked: File) => {
    const size = await imageSize(picked);
    if (size && Math.abs(size.width / size.height - aspectRatios[aspect]) / aspectRatios[aspect] > 0.01) {
      notify(`Это не ${aspect}: картинка ${size.width}×${size.height}. Нужна, например, ${aspect === "1:1" ? "1080×1080" : "1080×1350"}.`, undefined, "danger");
      return;
    }
    setBusy(aspect);
    try {
      await uploadPlacementAsset(subjectId, aspect, picked);
      await refresh();
      notify(`${subjectId}: ${aspect} загружен${live ? " — объявление уже в кабинете, скажите UA добавить квадрат в плейсмент ленты" : ""}`, undefined, "success");
    } catch (error) { notify(error instanceof Error ? error.message : `Не удалось загрузить ${aspect}`, undefined, "danger"); }
    finally { setBusy(null); }
  };
  const remove = async (aspect: PlacementAspect) => {
    setBusy(aspect);
    try {
      await act(`/api/placement-assets/${subjectId}/${placementAspectSlug(aspect)}`, { method: "DELETE" }, `${subjectId}: ${aspect} убран`);
    } catch (error) { notify(error instanceof Error ? error.message : `Не удалось убрать ${aspect}`, undefined, "danger"); }
    finally { setBusy(null); }
  };

  return <div className="placement-strip">
    {file && <span className="placement-chip placement-chip--base" title="Основной файл карточки — Reels и Stories">9:16 · основной</span>}
    {placementAspects.map((aspect) => {
      const asset = byAspect.get(aspect);
      if (!asset && !canAct) return null;
      return <span key={aspect} className={`placement-chip ${asset ? "placement-chip--set" : "placement-chip--empty"}`}
        title={`${aspect} — ${placementAspectLabels[aspect]}: тот же креатив под плейсмент ленты этого же объявления`}>
        {canAct && <input hidden type="file" accept="image/jpeg,image/png,image/webp" ref={(node) => { inputs.current[aspect] = node; }}
          onChange={(event) => { const picked = event.target.files?.[0]; event.target.value = ""; if (picked) void upload(aspect, picked); }} />}
        {asset
          ? <>
            <a href={asset.file.url} download={asset.file.originalName} target="_blank" rel="noreferrer">{aspect}</a>
            <small>{asset.width}×{asset.height}</small>
            {canAct && <button disabled={busy != null} title={`Заменить ${aspect}`} onClick={() => inputs.current[aspect]?.click()}><Upload size={11} /></button>}
            {canAct && <button disabled={busy != null} title={`Убрать ${aspect}`} onClick={() => void remove(aspect)}><X size={11} /></button>}
          </>
          : <button disabled={busy != null} onClick={() => inputs.current[aspect]?.click()}>
            <Plus size={11} /> {busy === aspect ? "загружаем…" : aspect}
          </button>}
      </span>;
    })}
  </div>;
}

/**
 * Что кабинет сказал об этом объявлении. Реджект модерации — самая частая
 * причина, по которой «ушедшее в открутку» крео стоит на нуле, и без этой
 * строки карточка выглядит просто как «ещё не набрало». Молчим только когда
 * сказать нечего: крео не уезжало или крутится штатно.
 *
 * GEN-2250: реджекты говорятся счётчиком по всем кабинетам, пауза и удаление
 * не говорятся вовсе; из слов кабинета осталось только «на модерации».
 */
function DeliveryNote({ creative }: { creative: Creative }) {
  const { data } = useAppState();
  const status = creative.deliveryStatus;
  const moderation = creative.moderation;
  if (moderation && moderation.rejected > 0) {
    const verdict = moderationVerdict(moderation, data.settings.moderation);
    return <div className={`delivery-note delivery-note--${verdict.status ? "rejected" : "paused"}`}>
      <TriangleAlert size={13} />
      <div>
        <b>Модерация: {moderation.rejected} из {moderation.launched} отклонено{verdict.status ? ` · ${moderationStatusLabels[verdict.status]}` : ""}</b>
        {moderation.rejectedAccounts.length > 0 && <p>Кабинеты: {moderation.rejectedAccounts.join(", ")}</p>}
        {creative.statusDetail && <p>Последний отказ: {creative.statusDetail}</p>}
      </div>
    </div>;
  }
  if (status === "pending_review") {
    return <div className={`delivery-note delivery-note--${status}`}>
      <TriangleAlert size={13} />
      <div>
        <b>Кабинет: {deliveryStatusLabels[status]}{creative.statusChangedAt ? ` · ${relativeTime(creative.statusChangedAt)}` : ""}</b>
        {creative.statusDetail && <p>{creative.statusDetail}</p>}
      </div>
    </div>;
  }
  if (creative.state === "live" && creative.metrics.spend === 0 && !status) {
    return <div className="delivery-note delivery-note--idle">
      <Radio size={13} />
      <div><b>В Media Library{creative.mlStatus ? ` как «${mediaLibraryStatusLabels[creative.mlStatus]}»` : ""}</b><p>Кабинет ещё не сообщал об открутке: UA не запустил объявление или статистика не приехала.</p></div>
    </div>;
  }
  return null;
}

/**
 * Разметка ссылки для кабинета. Без неё объявление открутится, но выручку к
 * этому крео уже не привязать: платёж находит своё крео по creative_uid из
 * url_tags. Поэтому блок видно ровно там же, где UA копирует ad_name — забрать
 * одно и не забрать второе не должно получаться по невнимательности.
 */
function AdTracking({ creative }: { creative: Creative }) {
  const { notify } = useAppState();
  if (creative.registrationError) {
    return <div className="ad-tracking ad-tracking--pending">
      <TriangleAlert size={13} />
      <span>Крео ещё не заведено в кабинете — разметки нет, метрики не приедут. Повтор идёт автоматически.</span>
    </div>;
  }
  if (!creative.urlTags) {
    return <div className="ad-tracking ad-tracking--pending">
      <TriangleAlert size={13} />
      <span>Разметка появится после отправки в Media Library (кнопка «В live») — до неё крео не заводится в кабинете.</span>
    </div>;
  }
  return <>
    {creative.mlStatus && <div className="ad-tracking">
      <span>Media Library: <b className={`ml-chip ml-chip--${creative.mlStatus}`}>{mediaLibraryStatusLabels[creative.mlStatus]}</b>{creative.mlStatus === "draft" ? " — UA ещё не запустил рекламу" : ""}</span>
    </div>}
    <div className="ad-name">
      <span title={creative.urlTags}>URL tags: {creative.urlTags}</span>
      <button
        aria-label="Скопировать разметку"
        onClick={() => { navigator.clipboard.writeText(creative.urlTags!); notify("Разметка скопирована"); }}
      ><Copy size={13} /></button>
    </div>
    <MediaDelivery creative={creative} />
  </>;
}

/**
 * Легло ли видео в бакет медиатеки. Отдельно от разметки: крео может быть
 * заведено, но файл не залит, и маркетолог тогда видит карточку без ролика.
 * Молчим только когда всё хорошо — успех и так виден тем, что файл на месте.
 */
function MediaDelivery({ creative }: { creative: Creative }) {
  if (!creative.file || creative.mediaState === "ready") return null;
  if (creative.mediaState === "failed") {
    return <div className="ad-tracking ad-tracking--pending">
      <TriangleAlert size={13} />
      <span title={creative.mediaError ?? undefined}>
        Видео не легло в бакет медиатеки — маркетолог его пока не увидит. Повтор идёт автоматически.
      </span>
    </div>;
  }
  if (creative.mediaState === "unfit") {
    // Повтора не будет: медиатека кабинета принимает только видео, и этот файл
    // её контракт не пройдёт никогда. Говорим это прямо — иначе продюсер ждёт
    // автоматического повтора, которого нет (GEN-1934).
    return <div className="ad-tracking ad-tracking--pending">
      <TriangleAlert size={13} />
      <span title={creative.mediaError ?? undefined}>
        Файл не уедет в медиатеку: {creative.mediaError ?? "не подходит под её контракт"}. Замените файл — доставка пойдёт заново.
      </span>
    </div>;
  }
  if (creative.mediaState === "pending") {
    return <div className="ad-tracking ad-tracking--pending">
      <CloudUpload size={13} />
      <span>Видео заливается в бакет медиатеки…</span>
    </div>;
  }
  return null;
}

/**
 * Паспорт гипотезы в шапке её карточки: когда завели, кто источник, в какой
 * неделе ушла в волну и сколько на ней уже потрачено. По одному факту всё это
 * лежало по разным экранам — а вопрос «сколько эта штука у нас живёт и во что
 * обошлась» возникает ровно здесь, при открытии карточки.
 */
function FamilyMeta({ family }: { family: Hypothesis }) {
  const spend = familySpend(family);
  const weeks = family.creatives.map((creative) => creative.week).filter(Boolean);
  // Компактный паспорт: одна тихая строка вместо таблицы фактов.
  // Автор и экономика уехали в самый верх шапки (FamilyStats) — здесь не дублируем.
  const facts = [
    weeks.length ? `W${String(Math.min(...weeks)).padStart(2, "0")}` : "не распакована",
    `${family.creatives.length} исп.`,
    ...(spend > 0 ? [] : ["$0"]),
    `KPI ${family.judge.replace("_", " ")}`,
    family.priority,
    family.source + (family.batch ? ` · ${family.batch}` : ""),
    `изм. ${relativeTime(family.updatedAt)}`
  ];
  return <div className="family-facts mono">{facts.join("  ·  ")}</div>;
}

/**
 * Линия эволюции в шапке семьи: корень, все его версии и место текущей.
 *
 * Версии — не колонки доски и не крео: каждая мерится своей парой, поэтому
 * рядом с номером стоит спенд и лучший CPS — по одной строке видно, дала ли
 * переработка что-то новое. Клик уводит на соседнюю версию, не закрывая доску.
 */
function FamilyLineage({ family }: { family: Hypothesis }) {
  const { data } = useAppState();
  const navigate = useNavigate();
  const root = family.id.replace(/-V\d+$/, "");
  const line = data.hypotheses
    .filter((item) => item.id === root || item.id.startsWith(`${root}-V`))
    .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
  if (line.length < 2) return null;
  return <div className="lineage">
    <span className="lineage__label">Линия</span>
    {line.map((item) => {
      const spend = familySpend(item);
      const cps = familyCps(item);
      const current = item.id === family.id;
      return <button
        key={item.id}
        className={`lineage__step${current ? " lineage__step--current" : ""}`}
        disabled={current}
        title={item.variationNote ?? item.title ?? item.statement}
        onClick={() => navigate(`/hypotheses/${item.id}`, { preserveScroll: true })}
      >
        <span className="mono">{item.id === root ? "V1" : `V${item.id.match(/-V(\d+)$/)?.[1]}`}</span>
        <em>{spend > 0 ? `${formatMoney(spend)}${cps != null ? ` · CPS ${formatMoney(cps, 2)}` : ""}` : "без открутки"}</em>
      </button>;
    })}
  </div>;
}

function FamilyModal({ family: boardFamily, close }: { family: Hypothesis; close: () => void }) {
  const { data, act, notify, refresh } = useAppState();
  const navigate = useNavigate();
  // Шаблон и мастер-скрипт облегчённая доска не несёт — берём из деталей;
  // крео галереи остаются из bootstrap, их держит в курсе живая лента.
  const slim = isSlimBootstrap(data);
  const detailQuery = useHypothesisDetail(boardFamily.id, slim);
  const detailReady = !slim || detailQuery.data != null;
  const family = slim ? withDetail(boardFamily, detailQuery.data, hypothesisDetailFields) : boardFamily;
  const [busy, setBusy] = useState<string | null>(null);
  const [extraProducer, setExtraProducer] = useState("motion");
  // Черновик null до первой правки — см. CreativeModal.
  const savedTemplate = templateFallback(family);
  const [templateEdit, setTemplateDraft] = useState<string | null>(null);
  const templateDraft = templateEdit ?? savedTemplate;
  // Шаблон задаёт структуру всех вариаций — правка структуры за Admin (§7 спеки).
  const canEditTemplate = data.user.role === "admin";
  const templateDirty = templateEdit != null && templateEdit !== savedTemplate;
  const saveTemplate = async () => {
    setBusy("template");
    try { await act(`/api/hypotheses/${family.id}/concept`, { method: "PATCH", body: JSON.stringify({ template: templateDraft.trim() || null }) }, "Шаблон сохранён"); }
    finally { setBusy(null); }
  };
  const copyTemplate = () => { navigator.clipboard.writeText(templateDraft); notify("Шаблон скопирован"); };
  const nextIndex = family.creatives.length
    ? Math.max(...family.creatives.map((creative) => Number(creative.id.match(/-C(\d+)/)?.[1] ?? 0))) + 1
    : 1;
  const addExtra = async () => {
    setBusy("add-extra");
    try { await act(`/api/hypotheses/${family.id}/creatives`, { method: "POST", body: JSON.stringify({ producer: extraProducer }) }, `${family.id}-C${nextIndex} добавлен в продакшн`); }
    finally { setBusy(null); }
  };
  // Партия: отметить несколько крео и перенести/отделить/удалить одним действием
  // («из H-025 три исполнения — в новый концепт»).
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<"move" | "split" | "delete" | null>(null);
  const pickedCreatives = family.creatives.filter((creative) => picked.has(creative.id));
  const togglePick = (creativeId: string) => setPicked((current) => {
    const next = new Set(current);
    if (next.has(creativeId)) next.delete(creativeId); else next.add(creativeId);
    return next;
  });
  const orderIds = family.creatives.map((creative) => creative.id);
  // Стрелки — это смена номера для мягкого крео: порядок показа и есть C-номер,
  // пока имя не заведено в кабинете (сервер перенумерует сам).
  const moveBy = async (creativeId: string, delta: number) => {
    const index = orderIds.indexOf(creativeId);
    const swap = index + delta;
    if (index < 0 || swap < 0 || swap >= orderIds.length) return;
    const next = [...orderIds];
    [next[index], next[swap]] = [next[swap]!, next[index]!];
    setBusy(`order-${creativeId}`);
    try { await act(`/api/hypotheses/${family.id}/creatives/order`, { method: "POST", body: JSON.stringify({ ids: next }) }, "Порядок обновлён — номера мягких крео пересчитаны"); }
    finally { setBusy(null); }
  };
  const readyCount = family.creatives.filter((creative) => creative.state === "ready_for_upload").length;
  const liveAll = async () => {
    setBusy("live-all");
    try { await act(`/api/hypotheses/${family.id}/live`, { method: "POST" }, `В Media Library ушло крео: ${readyCount}`); }
    finally { setBusy(null); }
  };
  const uploadSource = async (file: File) => {
    setBusy("source");
    try { await uploadHypothesisSource(family.id, file); await refresh(); notify("Оригинал прикреплён", undefined, "success"); }
    catch (error) { notify(error instanceof Error ? error.message : "Не удалось загрузить оригинал", undefined, "danger"); }
    finally { setBusy(null); }
  };
  // Иерархия — как в утверждённом макете: кто эта гипотеза (заголовок-утверждение,
  // теги, паспорт) → её крео галереей с бейджами положения → кастинг → шаблон.
  return <Modal wide onClose={close}><div className="family-modal">
    <header className="family-header">
      <div className="family-kicker">
        <span className="mono family-kicker__id">{family.id}</span>
        <StatusPill status={family.status} />
        {/* Автор — сразу в шапке: чья механика, видно до чтения утверждения. */}
        {family.author && <span className="author-chip" title="Автор гипотезы"><i>{family.author[0]}</i>{family.author}</span>}
        {family.product && data.settings.products.length > 1 && <span className="product-chip" title="Продукт">{family.product}</span>}
        {/* Вариация читается только вместе с родителем — его id стоит рядом. */}
        {family.parentId && <span className="lineage-chip" title={`Выросла из ${family.parentId}`}><GitBranch size={11} />{family.parentId}</span>}
        {/* Удаление отсюда закрывает саму модалку: её гипотезы больше нет. */}
        <div className="family-kicker__controls"><HypothesisControls family={family} onDeleted={close} /></div>
      </div>
      <h2 className="family-title">{family.title ?? family.statement}</h2>
      {/* Экономика семьи прямо под именем — как в шапке на доске: сколько слито
          из гейта, CPS и reject, решение видно без прокрутки к карточкам. */}
      {family.creatives.length > 0 && <FamilyStats family={family} />}
      {family.title && <p className="family-subtitle">{family.statement}</p>}
      {/* Что переработали — единственное, ради чего вариация отделена от
          родителя: без этой строки она читается как дубль карточки. */}
      {family.variationNote && <p className="family-variation-note"><strong>Переработано:</strong> {family.variationNote}</p>}
      <FamilyLineage family={family} />
      {family.description && <details className="family-description"><summary>подробнее</summary><p>{family.description}</p></details>}
      <div className="family-passport">
        <div className="family-tags"><Tag tone="pain">{family.pain}</Tag><span>→</span><Tag tone="desire">{family.desire}</Tag><Tag>{family.angle}</Tag>{family.tone && <Tag tone="accent">{family.tone}</Tag>}</div>
        <FamilyMeta family={family} />
      </div>
    </header>
    {(family.sourceVideo || family.creatives.length > 0 || data.user.role === "admin") && <>
      <div className="section-label section-label--row">
        <p className="section-label">{family.creatives.length ? "Крео этой гипотезы" : "Оригинал"}</p>
        {/* Вся гипотеза одним нажатием: уезжают все «Готово к заливу» с файлом. */}
        {["admin", "user"].includes(data.user.role) && readyCount > 0 && <Button variant="primary" loading={busy === "live-all"} onClick={liveAll}><Radio size={14} /> В live всё готовое ({readyCount})</Button>}
      </div>
      {pickedCreatives.length > 0 && <div className="family-bulk">
        <span>Выбрано {pickedCreatives.length}: {pickedCreatives.map((creative) => `C${creative.id.match(/-C(\d+)/)?.[1] ?? "?"}`).join(", ")}</span>
        <Button onClick={() => setBulk("move")}><Split size={14} /> Перенести в…</Button>
        <Button onClick={() => setBulk("split")}><GitBranchPlus size={14} /> Отделить в новую</Button>
        <Button variant="danger" onClick={() => setBulk("delete")}><Trash2 size={14} /> Удалить</Button>
        <button className="family-bulk__clear" onClick={() => setPicked(new Set())} aria-label="Снять выбор"><X size={14} /></button>
      </div>}
      <div className="family-grid">
        {family.sourceVideo ? <article className="family-creative family-original">
          <div className="family-creative__visual">
            <video src={`${family.sourceVideo.url}#t=0.1`} poster={`/api/files/${family.sourceVideo.id}/poster`} controls playsInline preload="metadata" aria-label="Оригинал-исходник" />
            <span className="family-original__badge">Оригинал</span>
          </div>
        </article> : data.user.role === "admin" && <label className="family-add family-add--original">
          <input hidden type="file" accept="video/mp4,video/quicktime,video/webm" onChange={(event) => event.target.files?.[0] && uploadSource(event.target.files[0])} />
          <div className="family-add__icon"><Film size={16} /></div>
          <strong>{busy === "source" ? "Загружаем…" : "+ оригинал"}</strong>
          <p>исходник, от которого пляшет семейство</p>
        </label>}

        {family.creatives.map((creative, index) => <article className={`family-creative ${creative.verdict === "winner" ? "family-creative--winner" : ""}`} key={creative.id}>
          {data.user.role === "admin" && <div className="family-creative__admin" onClick={(event) => event.stopPropagation()}>
            <button title={picked.has(creative.id) ? "Снять выбор" : "Выбрать для партии: перенести, отделить, удалить"} className={`family-creative__pick ${picked.has(creative.id) ? "is-picked" : ""}`} onClick={() => togglePick(creative.id)}>{picked.has(creative.id) ? <CheckSquare size={13} /> : <Square size={13} />}</button>
            <button title="Сдвинуть влево — мягкое крео получит меньший номер" disabled={index === 0 || busy != null} onClick={() => moveBy(creative.id, -1)}><ArrowLeft size={13} /></button>
            <button title="Сдвинуть вправо — мягкое крео получит больший номер" disabled={index === orderIds.length - 1 || busy != null} onClick={() => moveBy(creative.id, 1)}><ArrowRight size={13} /></button>
            <CreativeControls creative={creative} family={family} className="family-creative__menu" />
          </div>}
          <button className="family-creative__open" onClick={() => navigate(`/creatives/${creative.id}`, { preserveScroll: true })}>
            <div className="family-creative__visual">
              <CreativeVisual creative={creative} />
              {creative.verdict ? <VerdictPill verdict={creative.verdict} /> : <span className={`creative-state creative-state--${creative.state}`}>{creativeStateLabels[creative.state]}</span>}
            </div>
            <div className="family-creative__body">
              <span className="mono">C{creative.id.match(/-C(\d+)/)?.[1] ?? "?"}{creative.week ? ` · W${creative.week}` : ""}</span>
              <strong>{creative.variationTitle ?? creative.id}</strong>
              <div className="family-creative__meta"><b className={`producer producer--${creative.producer}`}>{creative.producer}</b><QaChip creative={creative} /><DeliveryChip creative={creative} /></div>
              <LocaleFlags creative={creative} />
              {creative.metrics.spend > 0 && <div className="mmini">
                <span>SP <b>{formatMoney(creative.metrics.spend)}</b></span>
                <span>HK <b>{formatPercent(creative.metrics.hookRate)}</b></span>
                <span>CTR <b>{formatPercent(creative.metrics.ctr, 2)}</b></span>
              </div>}
            </div>
          </button>
        </article>)}
        {family.creatives.length > 0 && data.user.role === "admin" && <div className="family-add">
          <div className="family-add__icon"><Plus size={18} /></div>
          <strong>+ вариация — C{nextIndex}</strong>
          <p>Сеттинг или веер хуков в той же строке гипотезы</p>
          <div className="family-add__controls">
            <Select value={extraProducer} onChange={(event) => setExtraProducer(event.target.value)} aria-label="Producer нового крео">
              <option value="claude">Claude</option>
              <option value="motion">Motion</option>
              <option value="julia">Julia</option>
            </Select>
            <Button variant="primary" loading={busy === "add-extra"} onClick={addExtra}><Plus size={15} /> Добавить</Button>
          </div>
        </div>}
      </div>
    </>}
    {!family.creatives.length && <section className="wave-unpack"><div className="wave-unpack__icon"><Split size={24} /></div><div><h3>Распаковать в честную пару</h3><p>C1 и C2 проверяют одно утверждение разными исполнениями. Данные будут сравниваться внутри семейства.</p></div><div className="producer-selects"><Button variant="primary" onClick={() => act(`/api/hypotheses/${family.id}/take-into-wave`, { method: "POST", body: JSON.stringify({ c1Producer: "motion", c2Producer: "motion" }) }, "Пара в продакшене")}>В продакшен</Button></div></section>}
    <p className="section-label">Шаблон (template) — общий скелет всех вариаций</p>
    <div className="family-template__body">
      <div className="script-head">
        <span>Свободный текст: арка, биты, локи — как удобно концепту</span>
        <div className="script-head__actions">
          {detailReady && templateDraft && <Button onClick={copyTemplate}><Copy size={14} /> Копировать</Button>}
          {canEditTemplate && templateDirty && <Button variant="primary" loading={busy === "template"} onClick={saveTemplate}>Сохранить</Button>}
        </div>
      </div>
      {!detailReady ? <DetailLoading failed={detailQuery.isError} retry={() => void detailQuery.refetch()} /> : canEditTemplate ? <textarea
        className="script-editor"
        value={templateDraft}
        onChange={(event) => setTemplateDraft(event.target.value)}
        placeholder={"Арка: строгость → сарказм → слом маски в тепло\n0–2s — гэг-открывашка, живой бит ломает серьёзность\n3–8s — реплика-запрет…\n\nЛоки: реплика один раз · бит каждые 2-3с"}
      /> : templateDraft ? <pre className="script-view">{templateDraft}</pre> : <p className="script-empty">Шаблона пока нет — попроси Claude в чате: «напиши шаблон сценария для {family.id}».</p>}
    </div>
    <AnimatePresence>
      {bulk === "move" && <MoveCreativesDialog family={family} creatives={pickedCreatives} mode="move" close={() => setBulk(null)} onDone={() => setPicked(new Set())} />}
      {bulk === "split" && <MoveCreativesDialog family={family} creatives={pickedCreatives} mode="split" close={() => setBulk(null)} onDone={() => setPicked(new Set())} />}
      {bulk === "delete" && <DeleteCreativesDialog creatives={pickedCreatives} close={() => setBulk(null)} onDone={() => setPicked(new Set())} />}
    </AnimatePresence>
  </div></Modal>;
}

/**
 * Вид «Открутка»: что уехало в кабинет и что с ним там. Отвечает на «какие
 * крео ушли в открутку, какие нет и почему» без раскопок по карточкам —
 * заведено ли, лежит ли в Media Library, запустил ли UA, что отклонила
 * модерация и с какой формулировкой, где упала регистрация или заливка.
 */
type DeliveryFilter = "all" | "idle" | "quarantine" | "unusable" | "active" | "issues" | "unfit";
const deliveryFilters: Array<{ id: DeliveryFilter; title: string }> = [
  { id: "all", title: "Всё уехавшее" },
  { id: "idle", title: "Не запущено" },
  { id: "active", title: "Крутится" },
  // Вместо одного «Реджекты» — два статуса модерации (GEN-2250): реджект
  // одного объявления из десяти не повод для вкладки, третий — уже да.
  { id: "quarantine", title: "Петушиный угол" },
  { id: "unusable", title: "Не используем" },
  { id: "issues", title: "Ошибки моста" },
  // Отдельно от «ошибок моста»: там то, что ещё повторяется само, здесь —
  // то, что мост больше не тронет, пока файл не заменят (GEN-1934). Счётчик
  // на вкладке и есть ответ на «сколько строк отложено», причина — в колонке
  // «Проблемы».
  { id: "unfit", title: "Не для медиатеки" }
];

/**
 * Сортировка доски (GEN-2250). «По дате» — свежее сверху: дата семьи — самый
 * поздний залив её крео и локалей в кабинет, а пока ничего не залито — дата
 * заведения. «Ручной порядок» — как отдаёт сервер: приоритет гипотезы.
 * «По CPS» — дешёвая подписка сверху; семьи без подписок (CPS ещё нет) уходят
 * вниз и между собой идут по дате.
 */
type BoardSort = "date" | "manual" | "cps";
const BOARD_SORT_KEY = "creo-loop:board-sort";
const DEFAULT_BOARD_SORT: BoardSort = "date";

function readBoardSort(): BoardSort {
  try {
    const stored = localStorage.getItem(BOARD_SORT_KEY);
    return stored === "manual" || stored === "cps" ? stored : DEFAULT_BOARD_SORT;
  } catch { return DEFAULT_BOARD_SORT; }
}

/** Пустой CPS всегда в хвосте, в какую бы сторону ни сортировали: «нет подписок» — не «дорого» и не «дёшево». */
function compareCps(left: number | null, right: number | null, descending = false) {
  if (left == null || right == null) return left == null ? (right == null ? 0 : 1) : -1;
  return descending ? right - left : left - right;
}

function sortFamilies(families: Hypothesis[], sort: BoardSort) {
  if (sort === "manual") return families;
  return families
    .map((item) => ({ item, date: familyDate(item), cps: sort === "cps" ? familyCps(item) : null }))
    .sort((left, right) => (sort === "cps" ? compareCps(left.cps, right.cps) : 0) || right.date.localeCompare(left.date))
    .map(({ item }) => item);
}

/** Сортировка строк таблицы по CPS кликом по заголовку: дешёвые сверху → дорогие сверху → как на доске. */
type TableSort = "cps-asc" | "cps-desc" | null;
const nextTableSort: Record<"none" | "cps-asc" | "cps-desc", TableSort> = { none: "cps-asc", "cps-asc": "cps-desc", "cps-desc": null };

function familyDate(family: Hypothesis) {
  let latest = family.createdAt;
  for (const creative of family.creatives) {
    for (const stamp of [creative.registeredAt ?? creative.createdAt, ...creative.localizations.map((item) => item.registeredAt ?? item.createdAt)]) {
      if (stamp > latest) latest = stamp;
    }
  }
  return latest;
}

function leftTheBoard(creative: Creative) {
  return Boolean(creative.creativeUid || creative.registeredAt || creative.deliveryStatus)
    || !["production", "ready_for_upload"].includes(creative.state);
}

/** Строка «Открутки» — крео или его локаль: у обоих одинаковый след в кабинете. */
type DeliverySubject = Pick<Creative, "id" | "creativeUid" | "registeredAt" | "deliveryStatus" | "statusDetail" | "statusChangedAt" | "mlStatus" | "mediaState" | "registrationError" | "mediaError" | "updatedAt" | "moderation"> & { spend: number };

function deliveryMatches(creative: DeliverySubject, filter: DeliveryFilter, thresholds: WorkspaceSettings["moderation"]) {
  switch (filter) {
    case "idle": return creative.spend === 0 && !creative.deliveryStatus;
    case "active": return creative.deliveryStatus === "active";
    case "quarantine":
    case "unusable": return moderationVerdict(creative.moderation, thresholds).status === filter;
    case "issues": return Boolean(creative.registrationError || creative.mediaError || creative.mediaState === "failed")
      && creative.mediaState !== "unfit";
    case "unfit": return creative.mediaState === "unfit";
    default: return true;
  }
}

/**
 * Где застряло незапущенное: уже лежит в Media Library и ждёт UA, файл ещё в
 * пути (заливается или доехал, но кабинет не ответил) — или дальше не уедет
 * само (ошибка заливки, файл не для медиатеки, файла нет).
 */
type DeliveryStage = "all" | "library" | "transit" | "stuck";
const deliveryStages: Array<{ id: DeliveryStage; title: string }> = [
  { id: "all", title: "Любая стадия" },
  { id: "library", title: "В Media Library" },
  { id: "transit", title: "Файл ещё едет" },
  { id: "stuck", title: "Застряло" }
];

function deliveryStage(subject: DeliverySubject): Exclude<DeliveryStage, "all"> {
  if (subject.mlStatus) return "library";
  return subject.mediaState === "pending" || subject.mediaState === "ready" ? "transit" : "stuck";
}

/** Сортировка «Открутки»: по дате отправки в кабинет в обе стороны или по гипотезе (H-002 раньше H-010). */
type DeliverySort = "sent-desc" | "sent-asc" | "hypothesis";
const naturalOrder = new Intl.Collator("ru", { numeric: true });

function DeliveryView({ families }: { families: Hypothesis[] }) {
  const navigate = useNavigate();
  const { data } = useAppState();
  const thresholds = data.settings.moderation;
  const [filter, setFilter] = useState<DeliveryFilter>("all");
  const [sort, setSort] = useState<DeliverySort>("sent-desc");
  const [producer, setProducer] = useState("all");
  const [kind, setKind] = useState<"all" | "creatives" | "locales">("all");
  const [stage, setStage] = useState<DeliveryStage>("all");
  const rows = useMemo(() => families
    .flatMap((family) => family.creatives.flatMap((creative) => {
      const own = leftTheBoard(creative)
        ? [{ family, creative, subject: { ...creative, spend: creative.metrics.spend } as DeliverySubject, localization: null as CreativeLocalization | null }]
        : [];
      // Локали — под-строками своего крео: уехавшие в кабинет или уже с его словом.
      const locales = creative.localizations
        .filter((item) => item.state === "live" || item.creativeUid || item.deliveryStatus)
        .map((item) => ({ family, creative, subject: { ...item, spend: item.metrics.spend } as DeliverySubject, localization: item }));
      return [...own, ...locales];
    }))
    .filter(({ creative, localization }) => (producer === "all" || creative.producer === producer)
      && (kind === "all" || (kind === "locales") === Boolean(localization)))
    .sort((left, right) => {
      const sent = (right.subject.registeredAt ?? right.subject.updatedAt).localeCompare(left.subject.registeredAt ?? left.subject.updatedAt);
      if (sort === "sent-asc") return -sent;
      if (sort === "hypothesis") return naturalOrder.compare(left.family.id, right.family.id) || naturalOrder.compare(left.subject.id, right.subject.id);
      return sent;
    }),
    [families, sort, producer, kind]);
  // Счётчики вкладок — по тем же producer и типу строк, но без стадии: стадия
  // уточняет только открытую вкладку.
  const counts = Object.fromEntries(deliveryFilters.map((entry) => [entry.id, rows.filter(({ subject }) => deliveryMatches(subject, entry.id, thresholds)).length])) as Record<DeliveryFilter, number>;
  const tabRows = rows.filter(({ subject }) => deliveryMatches(subject, filter, thresholds));
  const stageCounts = Object.fromEntries(deliveryStages.map((entry) => [entry.id, entry.id === "all" ? tabRows.length : tabRows.filter(({ subject }) => deliveryStage(subject) === entry.id).length])) as Record<DeliveryStage, number>;
  const visible = stage === "all" ? tabRows : tabRows.filter(({ subject }) => deliveryStage(subject) === stage);
  const sentSort = sort === "sent-asc" ? "ascending" : sort === "sent-desc" ? "descending" : "none";
  return <div className="delivery-view">
    <div className="delivery-toolbar">
      <div className="segmented">{deliveryFilters.map((entry) => <button key={entry.id} className={filter === entry.id ? "active" : ""} onClick={() => setFilter(entry.id)}>{entry.title} <b>{counts[entry.id]}</b></button>)}</div>
      <Select value={stage} onChange={(event) => setStage(event.target.value as DeliveryStage)} aria-label="Стадия доставки">
        {deliveryStages.map((entry) => <option key={entry.id} value={entry.id}>{entry.title} · {stageCounts[entry.id]}</option>)}
      </Select>
      <Select value={producer} onChange={(event) => setProducer(event.target.value)} aria-label="Producer">
        <option value="all">Все producer</option>
        {producers.map((name) => <option key={name} value={name}>{name}</option>)}
      </Select>
      <div className="segmented">
        <button className={kind === "all" ? "active" : ""} onClick={() => setKind("all")}>Всё</button>
        <button className={kind === "creatives" ? "active" : ""} onClick={() => setKind("creatives")}>Крео</button>
        <button className={kind === "locales" ? "active" : ""} onClick={() => setKind("locales")}>Локали</button>
      </div>
      <small>Слово кабинета приезжает с метриками раз в 15 минут; «не запущено» — заведено и лежит в Media Library, но UA его не включал.</small>
    </div>
    <div className="board-table-wrap"><table className="board-table delivery-table">
      <thead><tr><th>Крео</th><th aria-sort={sort === "hypothesis" ? "ascending" : "none"}><button className="th-sort" onClick={() => setSort("hypothesis")} title="Сортировка по номеру гипотезы">Гипотеза{sort === "hypothesis" ? " ↑" : ""}</button></th><th>Prod</th><th>Доска</th><th aria-sort={sentSort}><button className="th-sort" onClick={() => setSort(sort === "sent-desc" ? "sent-asc" : "sent-desc")} title="Сортировка по дате отправки в кабинет">Отправлено{sort === "sent-asc" ? " ↑" : sort === "sent-desc" ? " ↓" : ""}</button></th><th>Media Library</th><th>Кабинет</th><th>Модерация</th><th className="num">Spend</th><th>Проблемы</th></tr></thead>
      <tbody>
        {visible.map(({ family, creative, subject, localization }) => {
          const issues = [
            subject.registrationError ? `регистрация: ${subject.registrationError}` : null,
            subject.mediaError ? `медиа: ${subject.mediaError}` : null,
            subject.mediaState === "failed" && !subject.mediaError ? "заливка видео упала" : null,
            subject.mediaState === "unfit" && !subject.mediaError ? "файл не для медиатеки, доставка отложена" : null
          ].filter(Boolean) as string[];
          return <tr key={subject.id} className={`board-table__row ${localization ? "delivery-table__locale" : ""}`} onClick={() => navigate(`/creatives/${creative.id}`, { preserveScroll: true })}>
            <td className="board-table__title"><span className="board-table__cell">
              {localization
                ? <><span className="loc-flag loc-flag--live">{localizationTag(localization)}</span> <span className="mono">{localization.id}</span></>
                : <><span className="mono">{creative.id}</span> {creative.variationTitle ?? ""}</>}
            </span></td>
            <td><span className="mono board-table__hyp">{family.id}</span> {family.title ?? family.statement}</td>
            <td><b className={`producer producer--${creative.producer}`}>{creative.producer}</b></td>
            <td>{localization
              ? <span className={`creative-state creative-state--${localization.state}`}>{localizationStateLabels[localization.state]}</span>
              : creative.verdict ? <VerdictPill verdict={creative.verdict} /> : <span className={`creative-state creative-state--${creative.state}`}>{creativeStateLabels[creative.state]}</span>}</td>
            <td className="board-table__quiet" title={subject.registeredAt ?? undefined}>{subject.registeredAt ? formatDate(subject.registeredAt) : subject.creativeUid ? "заведено" : "—"}</td>
            <td>{subject.mlStatus ? <span className={`ml-chip ml-chip--${subject.mlStatus}`}>{mediaLibraryStatusLabels[subject.mlStatus]}</span> : <span className="board-table__quiet">{subject.mediaState === "ready" ? "файл доехал" : subject.mediaState === "pending" ? "заливается" : subject.mediaState === "unfit" ? "отложено" : "—"}</span>}</td>
            <td className="delivery-table__cabinet">
              {subject.deliveryStatus && subject.deliveryStatus !== "paused" && subject.deliveryStatus !== "deleted" && subject.deliveryStatus !== "rejected"
                ? <><DeliveryChip creative={subject} always />{subject.statusChangedAt && <small>{relativeTime(subject.statusChangedAt)}</small>}</>
                : <span className="board-table__quiet">{subject.spend > 0 ? "крутилось" : subject.deliveryStatus ? "—" : "не запущено"}</span>}
            </td>
            <td className="delivery-table__cabinet">
              {subject.moderation
                ? <><span className="meta-inline"><ModerationChip moderation={subject.moderation} thresholds={thresholds} /></span>{subject.moderation.rejected > 0 && subject.statusDetail && <p title={subject.statusDetail}>{subject.statusDetail}</p>}</>
                : <span className="board-table__quiet">—</span>}
            </td>
            <td className="num">{formatMoney(subject.spend)}</td>
            <td className="delivery-table__issues">{issues.length ? issues.map((issue) => <p key={issue} title={issue}>{issue}</p>) : <span className="board-table__quiet">—</span>}</td>
          </tr>;
        })}
        {!visible.length && <tr><td colSpan={10}><div className="column-empty"><Film size={17} /><span>Пока пусто</span></div></td></tr>}
      </tbody>
    </table></div>
  </div>;
}

/**
 * Колонка канбана рисуется порциями: в «В открутке» сотни семей, и рисовать
 * их все разом — секунды на каждый ререндер доски. Счётчик в шапке колонки
 * остаётся полным.
 */
const COLUMN_PAGE = 20;

function ColumnMore({ hidden, onMore }: { hidden: number; onMore: () => void }) {
  if (hidden <= 0) return null;
  return <button className="column-more" onClick={onMore}>
    <ChevronDown size={14} />Показать ещё {Math.min(COLUMN_PAGE, hidden)}{hidden > COLUMN_PAGE && <small>скрыто {hidden}</small>}
  </button>;
}

export function BoardPage() {
  const { data, act } = useAppState();
  const { upload: uploadVideo } = useCreativeUpload();
  const navigate = useNavigate();
  const { id, creativeId } = useParams();
  const [view, setView] = useState<"creatives" | "hypotheses" | "table" | "delivery">("creatives");
  const [createOpen, setCreateOpen] = useState(false);
  const [producer, setProducer] = useState("all");
  const [author, setAuthor] = useState("all");
  const [product, setProduct] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSortState] = useState<BoardSort>(readBoardSort);
  const [tableSort, setTableSort] = useState<TableSort>(null);
  const setSort = (value: BoardSort) => {
    setSortState(value);
    try { localStorage.setItem(BOARD_SORT_KEY, value); } catch { /* приватное окно — сортировка просто не запомнится */ }
  };
  const [hovered, setHovered] = useState<string | null>(null);
  const family = data.hypotheses.find((item) => item.id === id);
  const openCreative = useMemo(() => {
    for (const hypothesis of data.hypotheses) {
      const creative = hypothesis.creatives.find((item) => item.id === creativeId);
      if (creative) return { creative, family: hypothesis };
    }
    return null;
  }, [data.hypotheses, creativeId]);
  // Авторов не держим списком: поле свободное (Andrey, Julia, Motion, …), и новое
  // имя должно появляться в фильтре само, как только заведена первая гипотеза.
  const authors = useMemo(() => {
    const names = new Set<string>();
    let anonymous = false;
    for (const item of data.hypotheses) {
      if (item.author) names.add(item.author); else anonymous = true;
    }
    return { names: [...names].sort((a, b) => a.localeCompare(b, "ru")), anonymous };
  }, [data.hypotheses]);
  // Продукты, наоборот, справочник: список даёт настройка, но значения, снятые
  // со справочника (переименован/удалён), из фильтра пропадать не должны —
  // гипотезы с ними всё ещё на доске.
  const products = useMemo(() => {
    const known = new Set(data.settings.products);
    const orphaned = new Set<string>();
    for (const item of data.hypotheses) {
      if (item.product && !known.has(item.product)) orphaned.add(item.product);
    }
    return [...data.settings.products, ...[...orphaned].sort((a, b) => a.localeCompare(b, "ru"))];
  }, [data.settings.products, data.hypotheses]);
  const filtered = useMemo(() => data.hypotheses.filter((item) => {
    const matchesProducer = producer === "all" || item.creatives.some((creative) => creative.producer === producer);
    // Автор — свойство гипотезы, поэтому фильтр работает во всех видах доски,
    // а не только там, где видно крео.
    const matchesAuthor = author === "all" || (author === "none" ? !item.author : item.author === author);
    // Продукт — тоже гипотезный: NULL остался только у записей до миграции.
    const matchesProduct = product === "all" || item.product === product;
    const needle = query.toLowerCase();
    return matchesProducer && matchesAuthor && matchesProduct && (!needle || `${item.id} ${item.title ?? ""} ${item.statement} ${item.pain}`.toLowerCase().includes(needle));
  }), [data.hypotheses, producer, author, product, query]);
  const visible = useMemo(() => sortFamilies(filtered, sort), [filtered, sort]);
  const pristine = !query && author === "all" && product === "all" && producer === "all" && sort === DEFAULT_BOARD_SORT && tableSort == null;
  const resetView = () => {
    setQuery("");
    setAuthor("all");
    setProduct("all");
    setProducer("all");
    setSort(DEFAULT_BOARD_SORT);
    setTableSort(null);
  };
  // Лимиты колонок живут под ключом фильтров: сменили поиск, фильтр или
  // сортировку — ключ другой, и каждая колонка снова показывает первую порцию.
  const limitKey = JSON.stringify([query, author, product, producer, sort]);
  const [limits, setLimits] = useState<{ key: string; byColumn: Record<string, number> }>({ key: limitKey, byColumn: {} });
  const columnLimit = (columnId: string) => (limits.key === limitKey ? limits.byColumn[columnId] : undefined) ?? COLUMN_PAGE;
  const showMore = (columnId: string) => setLimits((current) => {
    const byColumn = current.key === limitKey ? current.byColumn : {};
    return { key: limitKey, byColumn: { ...byColumn, [columnId]: (byColumn[columnId] ?? COLUMN_PAGE) + COLUMN_PAGE } };
  });
  const upload = (creative: Creative, file: File) => uploadVideo(creative.id, file);
  // Плоские локали — настройка воркспейса: карточкой в своей колонке или, как
  // раньше, флажком внутри крео. Данные одни и те же, различается только чтение.
  const flat = data.settings.flatLocalizations;
  return <>
    <PageHeader eyebrow={`${currentWeekLabel()} · live workspace`} title="Доска производства" description="Гипотеза остаётся одной нитью, даже когда распадается на исполнения и версии." actions={<>{data.user.role === "admin" && <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> Гипотеза</Button>}</>} />
    <div className="board-toolbar">
      <div className="segmented" role="tablist" aria-label="Вид доски">
        <button className={view === "creatives" ? "active" : ""} onClick={() => setView("creatives")}>Доска</button>
        <button className={view === "table" ? "active" : ""} onClick={() => setView("table")}>Таблица</button>
        <button className={view === "hypotheses" ? "active" : ""} onClick={() => setView("hypotheses")}>Гипотезы</button>
        <button className={view === "delivery" ? "active" : ""} onClick={() => setView("delivery")}>Открутка</button>
      </div>
      <div className="search-box"><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="H-041, механика или утверждение" />{query && <button onClick={() => setQuery("")} aria-label="Очистить"><X size={13} /></button>}</div>
      <Select value={author} onChange={(event) => setAuthor(event.target.value)} aria-label="Автор гипотезы">
        <option value="all">Все авторы</option>
        {authors.names.map((name) => <option key={name} value={name}>{name}</option>)}
        {authors.anonymous && <option value="none">Без автора</option>}
      </Select>
      {products.length > 1 && <Select value={product} onChange={(event) => setProduct(event.target.value)} aria-label="Продукт">
        <option value="all">Все продукты</option>
        {products.map((name) => <option key={name} value={name}>{name}</option>)}
      </Select>}
      {view !== "delivery" && <Select value={sort} onChange={(event) => setSort(event.target.value as BoardSort)} aria-label="Сортировка">
        <option value="date">Сначала свежие</option>
        <option value="cps">По CPS: дешёвые сверху</option>
        <option value="manual">Ручной порядок</option>
      </Select>}
      {view === "creatives" && <div className="segmented"><button className={producer === "all" ? "active" : ""} onClick={() => setProducer("all")}>Все</button><button className={producer === "motion" ? "active" : ""} onClick={() => setProducer("motion")}>Motion</button></div>}
      {!pristine && <Button variant="ghost" className="board-reset" onClick={resetView} title="Убрать поиск и фильтры, вернуть сортировку «Сначала свежие»"><RotateCcw size={14} /> Сбросить</Button>}
      <span className="board-count"><Columns3 size={14} /> {visible.length} семейств</span>
    </div>
    {view === "creatives" && <div className="board-scroll"><div className="kanban">
      {columns.map((column) => {
        const entries = columnEntries(column, visible, flat);
        const count = column.id === "backlog"
          ? entries.length
          : entries.reduce((sum, entry) => sum + entry.creatives.length + entry.localizations.length, 0);
        const hint = column.id === "live" ? `набирает $${data.settings.gateSpend}` : column.hint;
        const limitId = `creatives:${column.id}`;
        const shown = entries.slice(0, columnLimit(limitId));
        return <section className="kanban-column" key={column.id}><header><div><span>{column.title}</span><b>{count}</b></div><small>{hint}</small></header><div className="kanban-stack">
          {shown.map(({ family: item, branches }) => {
            const framed = (column.id === "live" || column.id === "verdict" || column.id === "winners") && familySpend(item) > 0;
            return <div className="family-stack" key={item.id} onMouseEnter={() => setHovered(item.id)} onMouseLeave={() => setHovered(null)}>
            {/* Шапка семьи по макету GEN-1578: в измеряемых колонках — рамка с
                прогрессом до гейта, CPS и reject rate; решение видно без карточек. */}
            <div className={framed ? "family-stack__top--framed" : undefined}>
              <div className="family-stack__head">
                {/* Название механики — в шапке группы один раз, statement — подсказкой:
                    карточки под шапкой его больше не повторяют. */}
                <button className="family-label" title={item.statement} onClick={() => navigate(`/hypotheses/${item.id}`, { preserveScroll: true })}>
                  <span className="mono">{item.id}</span>
                  <strong className="family-label__title">{item.title ?? item.statement}</strong>
                </button>
                <HypothesisControls family={item} />
              </div>
              {framed && <FamilyStats family={item} />}
            </div>
            {!branches.length ? <div className="hypothesis-card">
              <button className="hypothesis-card__open" onClick={() => navigate(`/hypotheses/${item.id}`, { preserveScroll: true })}>
                <span>{item.priority} · {item.source}</span><strong>{item.title ?? item.statement}</strong><div><Tag tone="pain">{item.pain}</Tag><ArrowUpRight size={14} /></div>
              </button>
              {data.user.role === "admin" && <button className="hypothesis-card__wave" onClick={() => navigate(`/hypotheses/${item.id}`, { preserveScroll: true })}><Split size={13} /> В продакшен</button>}
            </div> : branches.map((branch) => <div className="creative-branch" key={branch.creative.id}>
              {/* Крео и его локали — одна ветка: карточка (или корешок, если
                  крео стоит в другой колонке), под ней локали со сдвигом. */}
              {branch.present
                ? <CreativeCard creative={branch.creative} family={item} sibling={hovered === item.id} onOpen={() => navigate(`/creatives/${branch.creative.id}`, { preserveScroll: true })} upload={(file) => upload(branch.creative, file)} />
                : <ParentStub creative={branch.creative} onOpen={() => navigate(`/creatives/${branch.creative.id}`, { preserveScroll: true })} />}
              {branch.localizations.map((localization) => <LocalizationCard
                key={localization.id}
                item={localization}
                creative={branch.creative}
                onOpen={() => navigate(`/creatives/${branch.creative.id}`, { preserveScroll: true })}
              />)}
            </div>)}
          </div>;
          })}
          <ColumnMore hidden={entries.length - shown.length} onMore={() => showMore(limitId)} />
          {!entries.length && <div className="column-empty"><Film size={17} /><span>Пока пусто</span></div>}
        </div></section>;
      })}
    </div></div>}
    {view === "hypotheses" && <div className="board-scroll"><div className="kanban kanban--concepts">
      {hypothesisColumns.map((column) => {
        const families = visible.filter((item) => column.statuses.includes(item.status));
        const limitId = `hypotheses:${column.id}`;
        const shown = families.slice(0, columnLimit(limitId));
        return <section className="kanban-column" key={column.id}><header><div><span>{column.title}</span><b>{families.length}</b></div><small>{column.hint}</small></header><div className="kanban-stack">
          {shown.map((item) => <HypothesisCard key={item.id} family={item} onOpen={() => navigate(`/hypotheses/${item.id}`, { preserveScroll: true })} />)}
          <ColumnMore hidden={families.length - shown.length} onMore={() => showMore(limitId)} />
          {!families.length && <div className="column-empty"><Film size={17} /><span>Пока пусто</span></div>}
        </div></section>;
      })}
    </div></div>}
    {view === "delivery" && <DeliveryView families={visible} />}
    {view === "table" && <div className="board-table-wrap"><table className="board-table">
      <thead><tr><th>Крео</th><th>Гипотеза</th><th>Prod</th><th>W</th><th className="num">Spend</th><th className="num">Hook</th><th className="num">Hold</th><th className="num">CTR</th><th className="num" aria-sort={tableSort === "cps-asc" ? "ascending" : tableSort === "cps-desc" ? "descending" : "none"}><button className="th-sort" onClick={() => setTableSort(nextTableSort[tableSort ?? "none"])} title="Сортировка внутри колонки по CPS: дешёвые → дорогие → как на доске">CPS {tableSort === "cps-asc" ? "↑" : tableSort === "cps-desc" ? "↓" : "↕"}</button></th></tr></thead>
      {columns.map((column) => {
        const entries = columnEntries(column, visible, flat);
        if (!entries.length) return null;
        // Локаль — такая же строка таблицы, как крео: своё имя в кабинете, свои
        // цифры. Со сдвигом и под своим крео, чтобы семья читалась.
        const rows = entries.flatMap(({ family: item, branches }) => branches.flatMap((branch) => [
          ...(branch.present ? [{ item, creative: branch.creative, localization: null as CreativeLocalization | null }] : []),
          ...branch.localizations.map((localization) => ({ item, creative: branch.creative, localization }))
        ]));
        // Под сортировкой по CPS строки перемешиваются между семьями: гипотеза
        // остаётся видна во втором столбце, а пустой CPS — внизу группы.
        if (tableSort) rows.sort((left, right) => compareCps((left.localization ?? left.creative).metrics.cpa, (right.localization ?? right.creative).metrics.cpa, tableSort === "cps-desc"));
        return <tbody key={column.id}>
          <tr className="board-table__group"><td colSpan={9}>{column.title}<b>{column.id === "backlog" ? entries.length : rows.length}</b></td></tr>
          {column.id === "backlog"
            ? entries.map(({ family: item }) => <tr key={item.id} className="board-table__row" onClick={() => navigate(`/hypotheses/${item.id}`, { preserveScroll: true })}>
              <td className="board-table__title"><span className="mono">—</span></td>
              <td><span className="mono board-table__hyp">{item.id}</span> {item.title ?? item.statement}</td>
              <td colSpan={7}><span className="board-table__quiet">{item.priority} · {item.source} · ещё без пары</span></td>
            </tr>)
            : rows.map(({ item, creative, localization }) => {
              const subject = localization ?? creative;
              return <tr key={subject.id} className={`board-table__row ${localization ? "board-table__row--locale" : ""}`} onClick={() => navigate(`/creatives/${creative.id}`, { preserveScroll: true })}>
              <td className="board-table__title"><span className="board-table__cell">
                <TableThumb file={subject.file} />
                {localization
                  ? <><span className={`loc-flag loc-flag--${localization.state}`}>{localizationTag(localization)}</span> <span className="mono">{localization.id}</span> <span className="board-table__quiet">{localizationStateLabels[localization.state]}</span></>
                  : <><span className="mono">C{creative.id.match(/-C(\d+)/)?.[1] ?? "?"}</span> {creative.variationTitle ?? creative.id}{creative.verdict && <> <VerdictPill verdict={creative.verdict} /></>}</>}
              </span></td>
              <td><span className="mono board-table__hyp">{item.id}</span> {item.title ?? item.statement}</td>
              <td><b className={`producer producer--${creative.producer}`}>{creative.producer}</b></td>
              <td className="board-table__quiet">{subject.week ? `W${subject.week}` : "—"}</td>
              <td className="num">{formatMoney(subject.metrics.spend)}</td>
              <td className="num">{formatPercent(subject.metrics.hookRate)}</td>
              <td className="num">{formatPercent(subject.metrics.holdRate)}</td>
              <td className="num">{formatPercent(subject.metrics.ctr, 2)}</td>
              <td className="num">{formatMoney(subject.metrics.cpa, 2)}</td>
            </tr>;
            })}
        </tbody>;
      })}
    </table></div>}
    <AnimatePresence>
      {createOpen && <CreateHypothesis close={() => setCreateOpen(false)} />}
      {family && <FamilyModal family={family} close={() => navigate("/board", { preserveScroll: true })} />}
      {openCreative && <CreativeModal creative={openCreative.creative} family={openCreative.family} close={() => navigate("/board", { preserveScroll: true })} />}
    </AnimatePresence>
  </>;
}
